import { ContentItem } from '@/types/cms';
import { USER_TESTIMONIALS } from "@/constants/videos";

export const case_studies: Record<string, ContentItem> = {
  "kehs": {
    id: "kehs",
    type: "case-study",
    category: "success-story",
    title: "AI-Powered Learning at King Edward VI High School",
    description: "Discover how a school in Birmingham is redefining the educational landscape with StudyStash's innovative AI-powered revision tools.",
    slug: "kehs",
    publishDate: "2023-11-15",
    readingTime: "4 min read",
    author: {
      name: "Ben Ward",
      image: "/team/ben.jpeg"
    },
    hero: {
      image: "/case-studies/kehs1.jpeg",
      imageAlt: "Founders of StudyStash presenting at King Edwards VI High School for Girls"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "King Edwards VI High School for Girls Case Study"
      },
      {
        type: "paragraph",
        text: "King Edward VI High School for Girls in Edgbaston, Birmingham, has transformed revision with StudyStash's innovative AI-powered tools, boosting confidence, improving organisation, and significantly saving students time each week."
      },
      {
        type: "statistics",
        stats: [
          {
            title: "Confidence",
            description: "of learners felt more confident with their UCAS content",
            value: "52%",
            color: "blue"
          },
          {
            title: "Time Saved",
            value: "4+ hours",
            description: "saved every week by using StudyStash",
            color: "green"
          },
          {
            title: "Learning Speed",
            value: "70%",
            description: "of students learnt faster using spaced repetition",
            color: "purple"
          }
        ],
        layout: "grid"
      },
      {
        type: "heading",
        level: 2,
        text: "Overview"
      },
      {
        type: "paragraph",
        text: "A leading institution renowned for academic excellence, the Birmingham based King Edward VI High School for Girls has joined the StudyStash pilot study to integrate our innovative platform into their curriculum. StudyStash generates unlimited AI-powered mindmaps, flashcards, tests, notes, and mini-games, creating tailored content for any subject in any language."
      },
      {
        type: "paragraph",
        text: "By automating note creation and employing evidence-based learning strategies, StudyStash enables students to revise more efficiently, retain information longer, and focus on what truly matters."
      },
      {
        type: "heading",
        level: 2,
        text: "Impact and Outcomes"
      },
      {
        type: "paragraph",
        text: "King Edward VI High School for Girls saw an immediate impact after onboarding StudyStash's AI-powered revision tools; students benefit from unlimited access to a variety of resources, including mind maps, flashcards, tests, notes, and interactive mini-games, available across all subjects and languages."
      },
      {
        type: "paragraph",
        text: "The results were evident, students are able to save hours of manual note-taking each week, learning faster, and retaining more information. Proven techniques like spaced repetition helped to boost memory retention by over 70% during our pilot, with over half of learners (52%) reporting increased confidence with their UCAS content, and students saving four hours per week thanks to AI-generated study materials. StudyStash has also revolutionized study organization, enabling students to structure revision efficiently, convert notes into interactive flashcards, and approach exams with greater clarity and confidence."
      },
      {
        type: "quote",
        text: "I've never been given anything like this before and it helped me so much having a place I could go to learn on my phone. Typically I find it hard to understand processes and equations but it was much easier when I used flashcards and mindmaps that I made for my GCSE's (Science mainly).",
        author: "KEHS Student",
        title: "Year 11, King Edwards VI High School for Girls",
        image: "/case-studies/kehs2.jpeg",
        logo: "/case-studies/KEHS_logo.png"
      },
      {
        type: "heading",
        level: 2,
        text: "Collaborative Learning Environment"
      },
      {
        type: "paragraph",
        text: "Beyond individual benefits, StudyStash helps to cultivate a dynamic and collaborative learning environment. By sharing mind maps, competing in mini-games, and exploring content together, revision is transformed from a solitary task into an engaging, social, and motivating experience. King Edward VI High School for Girls is not only keeping pace with the future of learning but actively shaping it, demonstrating that AI-driven education can lead to measurable results, smarter study habits, and a more confident and capable generation of students."
      }
    ],
    
    seo: {
      title: "AI-Powered Learning at King Edward VI High School | StudyStash",
      description: "Discover how a school in Birmingham is redefining the educational landscape with StudyStash's innovative AI-powered revision tools.",
      canonical: "https://studystash.com/case-studies/kehs"
    },
    
    relatedContent: ["wgs", "jcc", "afscs"]
  },

  "wgs": {
    id: "wgs",
    type: "case-study",
    category: "success-story",
    title: "Wolverhampton Grammar School: StudyStash Case Study",
    description: "Discover how a school in Wolverhampton is redefining the educational landscape with AI-powered revision tools.",
    slug: "wgs",
    publishDate: "2023-10-20",
    readingTime: "5 min read",
    author: {
      name: "Jonathan Graham",
      image: "/team/jonathan.png"
    },
    hero: {
      image: "/case-studies/wgs.JPG",
      imageAlt: "Wolverhampton Grammar School"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "Wolverhampton Grammar School Case Study"
      },
      {
        type: "paragraph",
        text: "Wolverhampton Grammar School has revolutionised their students' learning experience with StudyStash's innovative AI-powered revision tools, leading to improved confidence, better organisation, and enhanced academic performance."
      },
      {
        type: "statistics",
        stats: [
          {
            value: "1,300+",
            title: "Resources",
            description: "revision resources created (Mindmaps, Flashcards, Tests, Notes & Mini-games)",
            color: "blue"
          },
          {
            value: "65%+",
            title: "Confidence",
            description: "of students felt more confident going into their A-level exams",
            color: "green"
          },
          {
            value: "70%",
            title: "Organisation",
            description: "of students felt more organised and prepared with their A-levels",
            color: "purple"
          }
        ],
        layout: "grid"
      },
      {
        type: "heading",
        level: 2,
        text: "Overview"
      },
      {
        type: "paragraph",
        text: "WGS is a leading independent school in the UK, known for its academic excellence and innovative approach to education. StudyStash's AI-powered revision tools provide students with over 150 weekly AI-generated mindmaps, flashcards, tests, notes, and mini-games, creating an engaging and efficient study environment."
      },
      {
        type: "paragraph",
        text: "Students at WGS have been saving more than 4 hours per week by using StudyStash's revision tools instead of manually creating notes. Additionally, StudyStash's proven learning techniques like spaced repetition has increased memory retention by over 70%."
      },
      {
        type: "heading",
        level: 2,
        text: "Student Success Stories"
      },
      {
        type: "paragraph",
        text: "The impact on student performance has been remarkable. Students have reported: \"I couldn't have done gotten through my UCAS exams without you guys,\" and \"I wanted to give StudyStash a shout out as one of my top revision sites.\""
      },
      {
        type: "paragraph",
        text: "The platform has been particularly effective across multiple subjects, with one student noting: \"StudyStash helped me so much during my A-level exams (Maths, Physics, Bio).\""
      },
      {
        type: "quote",
        text: "I like how I can make my flashcards from my notes because it saves me a lot of time writing each one out and finding what to put on the front and the back. I really like the matching game to help me learn the flashcards too.",
        author: "Alex Morton",
        title: "A-Level Student, Apr. 2024, Wolverhampton Grammar School",
        image: "/case-studies/wgs.JPG",
        logo: "/case-studies/WGS_logo_circle.jpeg"
      }
    ],
    
    seo: {
      title: "Wolverhampton Grammar School Case Study | StudyStash",
      description: "Discover how a school in Wolverhampton is redefining the educational landscape with AI-powered revision tools.",
      canonical: "https://studystash.com/case-studies/wgs"
    },
    
    relatedContent: ["afscs", "jcc", "kehs"]
  },

  "jcc": {
    id: "jcc",
    type: "case-study",
    category: "success-story",
    title: "A forward-thinking college in Birmingham pilots StudyStash",
    description: "Joseph Chamberlain College (JCC) is leveling up their students' study and revision support with StudyStash's innovative AI-powered learning tools.",
    slug: "jcc",
    publishDate: "2023-09-25",
    readingTime: "4 min read",
    author: {
      name: "Ben Ward",
      image: "/team/ben.jpeg"
    },
    hero: {
      image: "/case-studies/jcc_classroom.jpg",
      imageAlt: "Students at Joseph Chamberlain College using StudyStash"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "Joseph Chamberlain College Case Study"
      },
      {
        type: "paragraph",
        text: "Joseph Chamberlain College has embraced StudyStash's innovative AI-powered revision tools, creating a more engaging, effective learning environment that has improved both student performance and confidence."
      },
      {
        type: "statistics",
        stats: [
          {
            value: "100+",
            title: "Learners",
            description: "learners creating Mindmaps, Flashcards, Tests and Notes",
            color: "blue"
          },
          {
            value: "30%+",
            title: "Learning Speed",
            description: "of learners said they learnt faster while using StudyStash",
            color: "green"
          },
          {
            value: "57%",
            title: "Support",
            description: "of learners felt more supported with their studies",
            color: "purple"
          }
        ],
        layout: "grid"
      },
      {
        type: "heading",
        level: 2,
        text: "Overview"
      },
      {
        type: "paragraph",
        text: "Joseph Chamberlain College is a prominent sixth form college located near Birmingham City Centre. It offers a wide range of academic and vocational programmes primarily designed as pathways to university education. It supports many of its students in progressing to higher education each year, making it a vital institution for local and surrounding communities."
      },
      {
        type: "paragraph",
        text: "With StudyStash's AI-powered tools, the students have been able to create customised resources including mindmaps, flashcards, and interactive tests. This has not only saved the students approximately 5 hours weekly but has also shown a significant increase in their exam performance."
      },
      {
        type: "heading",
        level: 2,
        text: "Student Impact and Results"
      },
      {
        type: "paragraph",
        text: "The implementation at Joseph Chamberlain College has yielded impressive results across their student body of 100+ learners who are actively creating resources including Mindmaps, Flashcards, Tests and Notes."
      },
      {
        type: "paragraph",
        text: "30% of learners reported that they learnt faster while using StudyStash, while 57% of learners said they were more supported with their studies using the platform. This demonstrates the significant impact of personalised, AI-powered learning tools."
      },
      {
        type: "quote",
        text: "I find the quizzes and flashcards quite helpful. They adjust based on the areas I struggle with the most, so I feel like my study time is always well spent. It seems a lot better than quizlet.",
        author: "JCC Student",
        title: "Sixth Form Student, Joseph Chamberlain College",
        image: "/case-studies/jcc_classroom.jpg",
        logo: "/case-studies/JCC_logo.jpeg"
      }
    ],
    
    seo: {
      title: "Joseph Chamberlain College Case Study | StudyStash",
      description: "Joseph Chamberlain College (JCC) is leveling up their students' study and revision support with StudyStash's innovative AI-powered learning tools.",
      canonical: "https://studystash.com/case-studies/jcc"
    },
    
    relatedContent: ["kehs", "wgs", "afscs"]
  },

  "afscs": {
    id: "afscs",
    type: "case-study",
    category: "success-story",
    title: "StudyStash: AFSCS helps students in Smethwick smash their exams",
    description: "Discover how a charity in Smethwick is using AI-Powered revision tools to support their students through their GCSE's and A-Levels.",
    slug: "afscs",
    publishDate: "2023-08-10",
    readingTime: "4 min read",
    author: {
      name: "Jonathan Graham",
      image: "/team/jonathan.png"
    },
    hero: {
      image: "/case-studies/AFSCS.jpeg",
      imageAlt: "Founders of StudyStash presenting at African French Speaking Community Support Charity"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "African French Speaking Community Support Case Study"
      },
      {
        type: "paragraph",
        text: "AFSCS, a charity organisation in Smethwick, has partnered with StudyStash to provide innovative AI-powered learning tools to support students from marginalised communities in their academic journey."
      },
      {
        type: "statistics",
        stats: [
          {
            value: "Digital & Linguistic",
            title: "Training",
            description: "training focused on digital and linguistic learning",
            color: "blue"
          },
          {
            value: "1.5+ hours",
            title: "Time Saved",
            description: "saved per resource on average by using StudyStash's AI",
            color: "green"
          },
          {
            value: "73%",
            title: "Learning Speed",
            description: "increase in learning speed vs traditional methods",
            color: "purple"
          }
        ],
        layout: "grid"
      },
      {
        type: "heading",
        level: 2,
        text: "Overview"
      },
      {
        type: "paragraph",
        text: "African French Speaking Community Support (AFSCS) is a charity organisation based in the West Midlands who supports those living on the margins of society. They provide English classes every Monday and Wednesday, a safe space for students and welcome anyone who comes their way."
      },
      {
        type: "paragraph",
        text: "AFSCS runs events to educate students about life skills like Technology, Science and Higher education. In June they even ran a 'Science Week' where hundreds of students learnt how to build sustainable buildings and code in Python."
      },
      {
        type: "heading",
        level: 2,
        text: "StudyStash Foundation Partnership"
      },
      {
        type: "paragraph",
        text: "AFSCS is a charity based in Smethwick. Jonathan and Ben, co-founders of StudyStash, were able to support AFSCS through the StudyStash foundation with a mission of helping local charities in the West Midlands."
      },
      {
        type: "paragraph",
        text: "This partnership represents StudyStash's commitment to supporting educational equality and providing access to innovative learning tools for all students, regardless of their background or circumstances."
      },
      {
        type: "heading",
        level: 2,
        text: "Implementation and Usage"
      },
      {
        type: "paragraph",
        text: "StudyStash provides an AI-powered platform for students to create revision notes on any topic, in any language, at any age range. There is GCSE, A-Level and even adult learners all creating digital resources focused on linguistic training to help them integrate with their local communities."
      },
      {
        type: "paragraph",
        text: "The platform's multilingual capabilities are particularly valuable for AFSCS students, many of whom are learning English as a second language while also pursuing academic qualifications."
      },
      {
        type: "quote",
        text: "StudyStash provides an AI-powered platform for students to create revision notes on any topic, in any language, at any age range. This has been transformative for our diverse student community.",
        author: "AFSCS Team",
        title: "African French Speaking Community Support",
        image: "/case-studies/AFSCS.jpeg",
        logo: "/case-studies/AFSCS_logo.png"
      }
    ],
    
    seo: {
      title: "AFSCS Charity Case Study | StudyStash",
      description: "Discover how a charity in Smethwick is using AI-Powered revision tools to support their students through their GCSE's and A-Levels.",
      canonical: "https://studystash.com/case-studies/afscs"
    },
    
    relatedContent: ["jcc", "kehs", "wgs"]
  },

  "coventry-university": {
    id: "coventry-university",
    type: "case-study",
    category: "award",
    title: "StudyStash Shortlisted for Midlands Young Professionals Awards",
    description: "Ben Ward and Jonathan Graham, co-founders of StudyStash, recognised by Coventry University Group as finalists for the Young Digital & Tech Professional of the Year at The Midlands Young Professionals Awards.",
    slug: "coventry-university",
    publishDate: "2023-12-15",
    readingTime: "3 min read",
    author: {
      name: "Ben Ward",
      image: "/team/ben.jpeg"
    },
    hero: {
      image: "/studystash_team.jpg",
      imageAlt: "The West Midlands Young Professional Awards (2023)"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "From Students Young Digital and Tech Professional Shortlistees"
      },
      {
        type: "paragraph",
        text: "While students at the University of Birmingham, our founders created StudyStash – an innovative platform combining AI and neuroscience to transform the learning experience for University and Higher Ed students."
      },
      {
        type: "heading",
        level: 2,
        text: "The Mission"
      },
      {
        type: "paragraph",
        text: "During their second year at university, Ben and Jonathan found themselves overwhelmed, faced with 300-page slide decks, endless tests, and hours of lectures that failed to stick. Instead of enduring the struggle, they set about to make a change."
      },
      {
        type: "paragraph",
        text: "Their mission: to transform education by creating a platform to empower every student, regardless of background, ability, or ambition, helping to unlock their full potential and achieve brilliant outcomes."
      },
      {
        type: "quote",
        text: "At this year's awards, Coventry University were proud to sponsor Young Digital and Tech Professional of the Year, with Paul Fairburn, the university's Director of Innovation, presenting to winner Ben Ward, Co-founder of revision resource platform, StudyStash.",
        author: "Paul Fairburn",
        title: "Director, Innovation Ecosystem, Coventry University",
        image: "/case-studies/paul-fairburn.jpeg",
        logo: "/coventry_university_logo.png"
      },
      {
        type: "heading",
        level: 2,
        text: "Changemaking"
      },
      {
        type: "paragraph",
        text: "Using proven AI-driven analytics paired with Cognitive Neuroscientific insights, they programmed StudyStash to identify where specific students were struggling to help you improve knowledge effectively and efficiently. In doing so, Ben and Jon were able to break away from the one-size-fits-all study model, creating a truly personalised learning environment tailored to individual learners' needs."
      },
      {
        type: "heading",
        level: 2,
        text: "Looking Ahead"
      },
      {
        type: "paragraph",
        text: "What began as a struggle to keep up is becoming a movement. Thousands of students already onboarded, partnerships growing, and our mission ever expanding: to make adaptive, personalised learning the norm, not the exception."
      },
      {
        type: "paragraph",
        text: "Being recognised by the Midlands Young Professionals Awards gave us real momentum, and was a powerful testament to the innovation behind StudyStash. Ready to see what's next? Join our 2026–2027 Pilot today."
      },
      {
        type: "cta-button",
        text: "Join our pilot program",
        href: "https://studystash.com/pilot-program"
      }
    ],
    
    seo: {
      title: "StudyStash shortlisted for Midlands Young Professionals Awards",
      description: "Ben Ward and Jonathan Graham, co-founders of StudyStash, recognised by Coventry University Group as finalists for the Young Digital & Tech Professional of the Year at The Midlands Young Professionals Awards.",
      canonical: "https://studystash.com/case-studies/coventry-university"
    },
    
    relatedContent: ["jaipals-story", "university-of-birmingham", "zaryabs-story"]
  },

  "jaipals-story": {
    id: "jaipals-story",
    type: "case-study",
    category: "success-story",
    title: "StudyStash Success Stories: Jaipal's A-Level Journey",
    description: "Former student at Wolverhampton Grammar School, Jaipal, achieved A*AAA in her Mathematics, Chemistry, Physics and Further Mathematics A-Levels! Find out how our AI-powered learning platform StudyStash helped to transform her learning journey into impressive attainment.",
    slug: "jaipals-story",
    publishDate: "2024-08-15",
    readingTime: "5 min read",
    author: {
      name: "Joanna Williams",
      image: "/team/joanna_williams.jpeg"
    },
    hero: {
      image: "/case-studies/jaipal_uppal.jpeg",
      imageAlt: "Jaipal at Wolverhampton Grammar School"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "From CBBA to A*A*AA"
      },
      {
        type: "paragraph",
        text: "Jaipal Uppal, an undergraduate Physics student at the University of Birmingham, took part in our pilot study with Wolverhampton Grammar School, where students used StudyStash in preparation for their A-Level exams. She studied Maths, Further Maths, Physics, Chemistry, and completed an EPQ. Jaipal credits StudyStash with helping her stay organised, manage her revision effectively, and save valuable time, allowing her to focus more on learning and supporting her A-Level success."
      },
      {
        type: "video",
        url: USER_TESTIMONIALS.Jaipal,
        title: "Jaipal Uppal, undergraduate Physics student at the University of Birmingham",
      },
      {
        type: "statistics",
        stats: [
          {
            title: "Grades",
            value: "A*AAA",
            description: "A-Level Grades in Maths, Chemistry, Physics, Further Maths",
            color: "blue"
          },
          {
            title: "Time Saved",
            value: "4+ hours",
            description: "saved weekly through organised revision",
            color: "green"
          },
          {
            title: "Advocacy",
            value: "100%",
            description: "would recommend to friends",
            color: "purple"
          }
        ],
        layout: "grid"
      },
      {
        type: "heading",
        level: 2,
        text: "About Jaipal"
      },
      {
        type: "paragraph",
        text: "I'm Jaipal, an undergraduate physics student at The University of Birmingham and I used StudyStash to achieve brilliant A-level results. For A-levels and my UCAS exams I studied Maths, Further Maths, Physics and Chemistry as well as an EPQ. StudyStash helped me so much to organise my revision, saving me time, and genuinely without your help I wouldn't have gotten the A-levels I did!"
      },
      {
        type: "heading",
        level: 2,
        text: "Overcoming Challenges"
      },
      {
        type: "paragraph",
        text: "My most difficult subject was chemistry; my strengths lie more with problem solving as opposed to memorising content and chemistry was my most content heavy topic. So, by using the features on StudyStash - mainly the flashcards with the AI spaced repetition feature - I was able to reinforce and retain a lot more information than before."
      },
      {
        type: "heading",
        level: 2,
        text: "Organisation and Study Methods"
      },
      {
        type: "paragraph",
        text: "I'd say I was fairly organised before, but StudyStash enabled me to do a lot all on one platform - like make folders and store notes with linked flashcards. It was a lot easier to find things than my system of just shoving physical flashcards in all of my draws (and they would often get lost)."
      },
      {
        type: "heading",
        level: 2,
        text: "Managing Stress and Support"
      },
      {
        type: "paragraph",
        text: "I think exams were one of the most stressful times in my personal life, which made my patience with academics and revision a lot thinner. The day before one of my chemistry exams I'd been using my StudyStash flashcards until I had an issue with my school account, I was ready to crack and I definitely needed sleep, but I sent a message through on the website illustrating my devastation and their team sorted it literally within half an hour. Feeling like the people behind the site actually wanted me to do well in my exams was one of the things that kept me motivated through the last week of A-levels."
      },
      {
        type: "heading",
        level: 2,
        text: "Subject Excellence"
      },
      {
        type: "paragraph",
        text: "I think StudyStash really helped with my chemistry as there were just so many recall questions."
      },
      {
        type: "heading",
        level: 2,
        text: "Favorite Features"
      },
      {
        type: "paragraph",
        text: "I really liked keeping a streak on StudyStash, it was just fun and the flashcards were great - they reminded me to re-look at some content that I wasn't sure of - and the software managed to separate the flashcards into groups of how well I knew them."
      },
      {
        type: "quote",
        text: "100% I have and will continue to do so!",
        author: "Jaipal Uppal",
        title: "Student, Wolverhampton Grammar School",
        image: "/case-studies/jaipal_uppal.jpeg",
        logo: "/case-studies/WGS_logo_circle.jpeg"
      }
    ],
    
    seo: {
      title: "Jaipal's A-Level Success Story | StudyStash",
      description: "Jaipal, student at Wolverhampton Grammar School achieved A*AAA in Mathematics, Chemistry, Physics and Further Mathematics and loves the StudyStash platform.",
      canonical: "https://studystash.com/case-studies/jaipals-story"
    },
    
    relatedContent: ["zaryabs-story", "jaimees-story", "kehs", "coventry-university"]
  },

  "jaimees-story": {
    id: "jaimees-story",
    type: "case-study",
    category: "success-story",
    title: "StudyStash Success Story: How Jaimee Increased her Exam Confidence",
    description: "Jaimee Adams, a second-year biomedical sciences student at the University of Birmingham, has been using StudyStash for just under a year. Find out how our AI-powered platform has helped to revamp her revision schedule, increase her knowledge and boost her confidence during the challenging run up to exams!",
    slug: "jaimees-story",
    publishDate: "2025-12-17",
    readingTime: "5 min read",
    author: {
      name: "Joanna Williams",
      image: "/team/joanna_williams.jpeg"
    },
    hero: {
      image: "/Jamiee.jpg",
      imageAlt: "Jaimee Adams, biomedical sciences student at the University of Birmingham"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "How Jaimee transformed her study struggles into success with StudyStash"
      },
      {
        type: "heading",
        level: 2,
        text: "About Jaimee"
      },
      {
        type: "paragraph",
        text: "I'm Jamiee, a second-year biomed student at the University of Birmingham. I've found studying and learning okay but obviously university can be challenging; trying to make good lecture notes and stay up to date with all the work."
      },
      {
        type: "paragraph",
        text: "Over the years I've tried to make some written notes and flashcards but I've struggled in finding an effective way to make flashcards online, other than writing them out beforehand. It was taking a lot of time to get everything together in one place. I'd also tried to do quiz questions or test my knowledge with mock exams, but again, I found it hard to find ones for myself that were to the right standard."
      },
      {
        type: "heading",
        level: 2,
        text: "The turning point"
      },
      {
        type: "paragraph",
        text: "The university introduced StudyStash in a lecture to us in around February, and I've found all of the features really helpful. Especially the flashcard tool and the test feature, because it's been able to actually make sure I understand the topics. Rather than just reading through content and assuming I know it, it's helped to actually embed it in my head. I noticed when I was learning my flashcards that I kept seeing specific topics, and I know they're coming up because I don't know it yet – that has been really helpful."
      },
      {
        type: "heading",
        level: 2,
        text: "Favourite features"
      },
      {
        type: "paragraph",
        text: "StudyStash has definitely improved my results from when I had exams in January versus the results I had in the summer which has been great. Also, because I'm able to access the flashcards daily, it is helping build confidence in what I'm learning. Actually understanding the content rather than just trying to cram it all in one in a matter of days is helping to gradually teach it to me."
      },
      {
        type: "paragraph",
        text: "It's also been really great to test my understanding for lectures and be able to summarise my notes so I know what the key points of every topic is; I don't have to go searching through 60 slides of PowerPoint information, I can find exactly what I need very quickly, which has been really helpful. StudyStash has just saved me so much time."
      },
      {
        type: "heading",
        level: 2,
        text: "In a nutshell"
      },
      {
        type: "paragraph",
        text: "I'd describe StudyStash as efficient, in terms of being able to summarise what I need very quickly. Helpful is probably the biggest one, and also accessible - because I'm able to access it on all my devices. It's so easy just to login and get straight into learning. Plus, when I've been traveling to/from anywhere, I always have it on my phone or my tablet so I just click on it straight away and do my flashcards or catch up with notes which is really handy."
      },
      {
        type: "heading",
        level: 2,
        text: "The verdict"
      },
      {
        type: "quote",
        text: "I definitely recommend StudyStash to other students, even just because it helps keep all your notes in one place and it's so useful to test your own understanding.",
        author: "Jaimee Adams",
        title: "Second-year Biomedical Sciences Student, University of Birmingham",
        image: "/Jamiee.jpg"
      }
    ],
    
    seo: {
      title: "Jaimee's Success Story: Increased Exam Confidence with StudyStash",
      description: "Jaimee Adams, a second-year biomedical sciences student at the University of Birmingham, shares how StudyStash helped revamp her revision schedule, increase her knowledge and boost her confidence during exams.",
      canonical: "https://studystash.com/case-studies/jaimees-story"
    },
    
    relatedContent: ["jaipals-story", "zaryabs-story", "university-of-birmingham"]
  },

  "zaryabs-story": {
    id: "zaryabs-story",
    type: "case-study",
    category: "success-story",
    title: "StudyStash Success Stories: Zaryab's A-level Journey",
    description: "Zaryab, student at Wolverhampton Grammar School used StudyStash to help him focus his learning, retain knowledge and ultimately achieve A*AA in his A-Level exams.",
    slug: "zaryabs-story",
    publishDate: "2024-08-20",
    readingTime: "6 min read",
    author: {
      name: "Joanna Williams",
      image: "/team/joanna_williams.jpeg"
    },
    hero: {
      image: "/case-studies/zaryab_hashmi.png",
      imageAlt: "Zaryab, WGS student"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "From ACC to A*AA"
      },
      {
        type: "paragraph",
        text: "Zaryab Hashmi, a student at Wolverhampton Grammar School, used StudyStash to achieve A*AA in his A-Level exams. His journey from struggling with GCSEs to excelling in his final exams demonstrates the power of our effective study tools and adaptive learning approaches."
      },
      {
        type: "video",
        url: USER_TESTIMONIALS.Zaryab,
        title: "Zaryab Hashmi, student at Wolverhampton Grammar School",
      },
      {
        type: "statistics",
        stats: [
          {
            value: "A*AA",
            title: "Grades",
            description: "would recommend to friends",
            color: "blue"
          },
          {
            value: "5",
            title: "Subjects",
            description: "History, Mathematics, Biology, English Literature, Geography",
            color: "purple"
          },
          {
            value: "80%",
            title: "Improvement",
            description: "Improvement in Self-Assessed Test Scores",
            color: "green"
          }
        ],
        layout: "grid"
      },
      {
        type: "heading",
        level: 2,
        text: "The Challenge"
      },
      {
        type: "paragraph",
        text: "I didn't really take my GCSEs seriously at all – and to make things worse, I didn't even take history at GCSE; at the start of my A-Level journey I was trying to figure out how to revise effectively for history because I hadn't done it before. Meanwhile, it seemed like everyone else was racing ahead of me. On top of that I had to deal with the huge jump from Year 11 to Year 12 that nobody seems to warn you about."
      },
      {
        type: "heading",
        level: 2,
        text: "StudyStash Integration"
      },
      {
        type: "paragraph",
        text: "StudyStash became an integral part of my revision routine. I was able to learn content much faster by regularly doing flashcard sessions on specific topics. All I had to do was upload my own notes, and the AI tool would do the rest for me. In minutes, I had a set of flashcards on a topic I needed to learn, and I could review them whenever I found the time. Once I felt more confident, I'd ask the Ai teaching-learning assistant to generate a test so I could assess myself, identify the topics that needed the most work, and strengthen my understanding."
      },
      {
        type: "heading",
        level: 2,
        text: "Building Confidence"
      },
      {
        type: "paragraph",
        text: "At first, I was reluctant to contribute in class because I saw myself as someone who couldn't add much, and felt I was at the bottom of the class. However, after using StudyStash and seeing my grades and test results improve steadily, I found myself participating in class and growing more confident in presenting my ideas. This newfound confidence helped me outside the classroom as well, as I was given opportunities to deliver talks both externally and at school."
      },
      {
        type: "heading",
        level: 2,
        text: "Stress Management"
      },
      {
        type: "paragraph",
        text: "Stress was, and still is, a big part of my A-level journey. I remember panicking about my UCAS exams, not knowing how to revise, and giving up time and time again. However, thanks to the people around me and StudyStash, I was able to develop a system that works for me. As soon as I started learning the content, I felt more confident in myself, and the stress gradually decreased."
      },
      {
        type: "heading",
        level: 2,
        text: "Recommendation"
      },
      {
        type: "paragraph",
        text: "When I discovered StudyStash, it became my main resource for revision. I made notes on it and converted those notes into flashcards. To be honest, I didn't need to use anything else, and I don't think I could go back! StudyStash is an amazing resource that I would recommend to everyone. You can use it for everything, from notes and mind maps to flashcards and even topic tests. The way it uses AI helps you not just get through exams but actually learn from the material!"
      },
      {
        type: "quote",
        text: "StudyStash is an amazing resource that I would recommend to everyone. You can use it for everything - from notes and mind maps to flashcards and even topic tests. The way it uses AI helps you not just get through exams but actually learn from the material!",
        author: "Zaryab Hashmi",
        title: "Student, Wolverhampton Grammar School",
        image: "/case-studies/zaryab_hashmi.png",
        logo: "/case-studies/WGS_logo_circle.jpeg"
      }
    ],
    
    seo: {
      title: "Zaryab's A-Level Success Story | StudyStash",
      description: "Zaryab, student at Wolverhampton Grammar School used StudyStash to help him focus his learning, retain knowledge and ultimately achieve A*AA in his A-Level exams.",
      canonical: "https://studystash.com/case-studies/zaryabs-story"
    },
    
    relatedContent: ["jaipals-story", "university-of-birmingham", "wgs"]
  },

  "university-of-birmingham-press-release": {
    id: "university-of-birmingham-press-release",
    type: "case-study",
    category: "partnership",
    title: "Student entrepreneurs score business success with University-wide education enhancement project",
    description: "Computer Science students use their study experiences to design AI-driven platform to reduce exam stress, boost confidence and improve academic performance.",
    slug: "university-of-birmingham-press-release",
    publishDate: "2024-09-01",
    readingTime: "8 min read",
    author: {
      name: "Ben Ward",
      image: "/team/ben.jpeg"
    },
    hero: {
      image: "/team.jpg",
      imageAlt: "StudyStash founders Jonathan Graham and Ben Ward (centre) with student members of their team"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "University of Birmingham Press Release"
      },
      {
        type: "paragraph",
        text: "Two student entrepreneurs at the University of Birmingham have developed StudyStash, an innovative learning platform that combines AI and cutting-edge neuroscience to personalise study content and adapt the learning process to each individual's unique needs."
      },
      {
        type: "heading",
        level: 2,
        text: "The StudyStash Platform"
      },
      {
        type: "paragraph",
        text: "Founders Ben Ward and Jonathan Graham created StudyStash while revising for their second-year Computer Science exams, based on their own study experiences. They designed the platform to reduce exam stress, boost confidence and improve overall academic performance."
      },
      {
        type: "paragraph",
        text: "The multilingual StudyStash platform uses AI to analyse lecture notes and course materials to generate a range of bespoke learning and revision aids, including flashcards, tests, games, revision schedules and peer interactions. AI is also used to adapt the learning process to the unique needs and style of each user, with students able to access their personal learning schedule and content 24/7 with mobile devices such as smartphones."
      },
      {
        type: "heading",
        level: 2,
        text: "University-Wide Pilot"
      },
      {
        type: "paragraph",
        text: "Supported by the University's student start-up programme, UoB Elevate, the pair have now moved the fledgling business to the next level. University of Birmingham education experts have worked with Ben and Jonathan to design and roll out a year-long, University-wide pilot of the platform, as part of a next stage of the start-up programme UoB Elevate PLUS."
      },
      {
        type: "paragraph",
        text: "The pilot will involve over 5,000 students in taught modules across a diverse range of subjects including Computer Science, Medicine, Law, Sports Science, Chemical Engineering and Politics."
      },
      {
        type: "heading",
        level: 2,
        text: "Benefits for Lecturers and Students"
      },
      {
        type: "paragraph",
        text: "Lecturers can use the platform to identify how well students are engaging with their course and where they may need extra support - helping staff to adapt teaching to improve student attainment and course satisfaction."
      },
      {
        type: "paragraph",
        text: "Alongside the pilot, researchers will formally analyse the platform's impact on the study experience and academic results - examining how students and staff benefit from the technology and how StudyStash can maximise its impact."
      },
      {
        type: "heading",
        level: 2,
        text: "Founder Perspectives"
      },
      {
        type: "paragraph",
        text: "We created StudyStash for ourselves - building the entire platform from scratch - and it helped us get great exam results. The platform has evolved to provide everything students need to smash their exams. It's like having a personal tutor on hand 24/7, learning from how you study and helping you to revise like a pro. The real-world stories from the many thousands of users we've helped to date give us great confidence we can deliver brilliant learning outcomes for students of all abilities and backgrounds."
      },
      {
        type: "paragraph",
        text: "There's a potentially huge benefit for lecturers - giving them more time to focus on students who need one-to-one support. They also have the option of integrating StudyStash into their teaching programme and tracking students' progress - allowing them to adjust teaching according to their students' needs. This is a real game-changer for the academic teaching community."
      },
      {
        type: "quote",
        text: "The pilot is hugely exciting and offers us an excellent opportunity to explore how we can use education technologies to further enhance student support and the student experience at the University of Birmingham.",
        author: "Professor Deborah Longworth",
        title: "Pro-Vice-Chancellor (Education), University of Birmingham",
        image: "/deborah.png",
        logo: "/uob_crest.png"
      },
      {
        type: "quote",
        text: "Ben and Jonathan have the skills and passion to build their business, while studying full-time for their degrees. As an alumnus, I'm delighted to guide and support them with a career's worth of experience in the software industry, and hopefully see their business flourish.",
        author: "Gareth Noyes",
        title: "StudyStash Advisory Board Member",
        image: "/gareth.png",
        logo: "/favicon-32x32.png"
      },
      {
        type: "heading",
        level: 2,
        text: "Advisory Board and Support"
      },
      {
        type: "paragraph",
        text: "High-profile supporters who have joined the young entrepreneurs' business advisory board include US-based alumnus and tech expert Gareth Noyes, Tech WM chair Jason Sahota and University of Birmingham Pro-Vice-Chancellor (Education) Professor Deborah Longworth."
      },
      {
        type: "paragraph",
        text: "Ben and Jonathan received a Start-up Scholarship - made possible by alumni philanthropy - and support from UoB Elevate in developing their business to become a viable, commercial venture."
      }
    ],
    
    seo: {
      title: "University of Birmingham Press Release | StudyStash",
      description: "Computer Science students use their study experiences to design AI-driven platform to reduce exam stress, boost confidence and improve academic performance.",
      canonical: "https://studystash.com/case-studies/university-of-birmingham"
    },
    
    relatedContent: ["jaipals-story", "zaryabs-story", "kehs"]
  },

  "dash-virdee-nomination": {
    id: "dash-virdee-nomination",
    type: "case-study",
    category: "award",
    title: "Dash Virdee Nominates StudyStash for Lord Mayor's Award",
    description: "StudyStash has been nominated for the prestigious Lord Mayor's Award by Dash Virdee, recognising our innovative contribution to education technology in Birmingham.",
    slug: "dash-virdee-nomination",
    publishDate: "2025-01-15",
    readingTime: "3 min read",
    author: {
      name: "StudyStash Team",
      image: "/team/ben.jpeg"
    },
    hero: {
      image: "/lords_mayors_award.png",
      imageAlt: "Lord Mayor's Award logo"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "Dash Virdee's Nomination: A Recognition of Innovation"
      },
      {
        type: "paragraph",
        text: "We're thrilled to announce that StudyStash has been nominated for the Lord Mayor's Award by Dash Virdee, a respected figure in Birmingham's business and education community. This prestigious nomination recognises our innovative contribution to education technology and our positive impact on student learning outcomes."
      },
      {
        type: "statistics",
        stats: [
          {
            value: "Prestigious",
            title: "Award",
            description: "Lord Mayor's Award nomination",
            color: "blue"
          },
          {
            value: "Dash Virdee",
            title: "Nominator",
            description: "respected community leader",
            color: "green"
          },
          {
            value: "Birmingham",
            title: "Recognition",
            description: "innovation hub recognition",
            color: "purple"
          }
        ],
        layout: "grid"
      },
      {
        type: "heading",
        level: 2,
        text: "About Dash Virdee"
      },
      {
        type: "paragraph",
        text: "Dash Virdee is a well-respected figure in Birmingham's business and education community, known for his commitment to supporting innovative startups and educational initiatives that make a real difference in the city."
      },
      {
        type: "paragraph",
        text: "With extensive experience in both business and education sectors, Dash has been instrumental in fostering innovation and supporting organisations that contribute to Birmingham's growth and development."
      },
      {
        type: "heading",
        level: 2,
        text: "The Nomination"
      },
      {
        type: "paragraph",
        text: "Dash's nomination of StudyStash for the Lord Mayor's Award reflects his recognition of our innovative approach to education technology and the positive impact we've had on student learning outcomes across Birmingham and beyond."
      },
      {
        type: "paragraph",
        text: "This nomination is particularly meaningful as it comes from someone who deeply understands both the challenges in education and the potential of technology to address them effectively."
      },
      {
        type: "quote",
        text: "StudyStash represents exactly the kind of innovative thinking and practical impact that Birmingham needs. Their AI-powered learning platform is transforming education and deserves recognition for its contribution to student success.",
        author: "Dash Virdee",
        title: "Lord Mayor's Award Nominator",
        image: "/lords_mayors_award.png",
        logo: "/lords_mayors_award.png"
      },
      {
        type: "heading",
        level: 2,
        text: "What This Means for StudyStash"
      },
      {
        type: "paragraph",
        text: "Being nominated for the Lord Mayor's Award by someone of Dash's standing validates our mission to transform education through AI-powered learning solutions. It reinforces our belief that technology can make a real difference in how students learn and succeed."
      },
      {
        type: "paragraph",
        text: "This recognition also highlights the importance of local support and community engagement in building successful educational technology companies that truly serve their users."
      }
    ],
    
    seo: {
      title: "Dash Virdee Nominates StudyStash for Lord Mayor's Award | StudyStash",
      description: "StudyStash has been nominated for the prestigious Lord Mayor's Award by Dash Virdee, recognising our innovative contribution to education technology in Birmingham.",
      canonical: "https://studystash.com/about/blogs/dash-virdee-nomination"
    },
    
    relatedContent: ["university-of-birmingham", "coventry-university", "jaipals-story"]
  },

  "eab-partnership": {
    id: "eab-partnership",
    type: "case-study",
    category: "partnership",
    title: "StudyStash Partners with EAB to Complete the Teaching-Learning Loop",
    description: "StudyStash announces partnership with EAB, the leading higher education research and technology company serving more than 2,500 institutions worldwide.",
    slug: "eab-partnership",
    publishDate: "2025-01-20",
    readingTime: "6 min read",
    author: {
      name: "Ben Ward",
      image: "/team/ben.jpeg"
    },
    hero: {
      image: "/EAB-blue-bg.png",
      imageAlt: "EAB x StudyStash Partnership"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "StudyStash Partners with EAB to Complete the Teaching-Learning Loop"
      },
      {
        type: "paragraph",
        text: "StudyStash, the innovative learning platform that uses AI and neuroscience to deliver personalised and adaptive learning, today announced its partnership with EAB, the leading higher education research and technology company that serves more than 2,500 institutions worldwide."
      },
      {
        type: "statistics",
        stats: [
          {
            value: "83%",
            title: "Confidence",
            description: "of students reported increased confidence managing study workload",
            color: "blue"
          },
          {
            value: "70%",
            title: "Stress Reduction",
            description: "of students reported reduced stress",
            color: "green"
          },
          {
            value: "55%",
            title: "International Students",
            description: "of StudyStash users were international students",
            color: "purple"
          }
        ],
        layout: "grid"
      },
      {
        type: "heading",
        level: 2,
        text: "How the Partnership Began"
      },
      {
        type: "paragraph",
        text: "The partnership originated from StudyStash's successful pilot at the University of Birmingham, involving over 5,000 students. The impressive results caught the attention of Deborah Longworth, Pro-Vice-Chancellor of Education at the University of Birmingham, who introduced StudyStash to EAB, recognising the potential for scaling these proven outcomes across higher education institutions globally."
      },
      {
        type: "heading",
        level: 2,
        text: "Proven Results Drive Partnership"
      },
      {
        type: "paragraph",
        text: "The Birmingham pilot demonstrated measurable impact with 83% of students reporting increased confidence managing study and revision workload, 70% of students reporting reduced stress, and 55% of StudyStash users being international students."
      },
      {
        type: "heading",
        level: 2,
        text: "Learning Intelligence That Actually Delivers"
      },
      {
        type: "paragraph",
        text: "StudyStash transforms course content into personalised study experiences while providing weekly intelligence that eliminates guesswork about where students need help. The platform delivers three key benefits:"
      },
      {
        type: "paragraph",
        text: "For Students: Transform overwhelming course materials into confidence-building study experiences with AI-powered tools and gamified engagement features."
      },
      {
        type: "paragraph",
        text: "For Faculty: Reduced workload through weekly intelligence reports and StudyChat AI handling initial student questions, enabling more targeted support."
      },
      {
        type: "paragraph",
        text: "For Institutions: Protect revenue through early identification of at-risk students while maximising faculty efficiency without disrupting existing teaching methods."
      },
      {
        type: "quote",
        text: "What I really like about StudyStash is that it simplifies the revision process, but still draws on all the neuroscience of learning.",
        author: "Benjamin Ellis",
        title: "Head of School (Education) of School of Sport, Exercise and Rehabilitation Sciences, University of Birmingham",
        image: "/team/ben.jpeg",
        logo: "/uob_crest.png"
      },
      {
        type: "heading",
        level: 2,
        text: "EAB CONNECTED25 Collaboration"
      },
      {
        type: "paragraph",
        text: "StudyStash and EAB will be presenting together at EAB CONNECTED25 in Orlando, Florida, showcasing the partnership and sharing insights from the University of Birmingham success story."
      }
    ],
    
    seo: {
      title: "StudyStash Partners with EAB | StudyStash",
      description: "StudyStash announces partnership with EAB, the leading higher education research and technology company serving more than 2,500 institutions worldwide.",
      canonical: "https://studystash.com/about/blogs/eab-partnership"
    },
    
    relatedContent: ["university-of-birmingham", "9-to-thrive-podcast", "innovators-uncensored"]
  },

  "st-marys-university": {
    id: "st-marys-university",
    type: "case-study",
    category: "partnership",
    title: "St Mary's University pilots adaptive learning platform",
    description: "St Mary's University partners with StudyStash to pilot AI-powered adaptive learning, supporting student attainment, engagement, and retention through responsible AI.",
    slug: "st-marys-university",
    publishDate: "2025-10-24",
    readingTime: "3 min read",
    author: {
      name: "Joanna Williams",
      image: "/team/joanna_williams.jpeg"
    },
    hero: {
      image: "/case-studies/st_marys_campus.jpg",
      imageAlt: "St Mary's University"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "St Mary's University, Twickenham joins forces with StudyStash to pilot student-centred adaptive learning platform"
      },
      {
        type: "paragraph",
        text: "St Mary's University, Twickenham has announced a new partnership with StudyStash, an AI-powered learning platform built by students, for students, to pilot innovative ways of supporting student attainment, engagement, and retention through responsible artificial intelligence."
      },
      {
        type: "paragraph",
        text: "The pilot will see our platform integrated directly within St Mary's online learning environment, providing students with personalised, adaptive study tools that respond to their individual learning needs. StudyStash uses AI to transform lecture notes, slides, and course content into tailored revision resources, including quizzes, flashcards, and smart study schedules, while maintaining the highest standards of data ethics and academic integrity."
      },
      {
        type: "paragraph",
        text: "Following on from successful rollouts at the University of Birmingham and Loughborough University, at St Mary's, the focus will be on community-driven learning, wellbeing, and academic balance, reflecting the university's strong student-centred ethos."
      },
      {
        type: "heading",
        level: 2,
        text: "A focus on learning belonging and balance"
      },
      {
        type: "paragraph",
        text: "The St Mary's pilot will include all online learners, across courses like Computer Science and Marketing, as well as programmes in Education, Psychology, and MBA pathways, exploring how AI can reduce academic stress, build confidence, and strengthen independent learning skills. By integrating directly within the LMS, students will be able to access their personalised study materials without switching platforms, giving them an easier, more intuitive way to stay organised and on track."
      },
      {
        type: "quote",
        text: "St Mary's has always prioritised the experience and success of our students. Our implementation of StudyStash within Canvas provides a great way to help all online students stay engaged with their courses and achieve academic success.",
        author: "Kevin Braim",
        title: "CIO at St Mary's University",
        image: "/kevin_braim.png",
        logo: "/st_marys_crest.png"
      },
      {
        type: "heading",
        level: 2,
        text: "Student-centred, always"
      },
      {
        type: "paragraph",
        text: "StudyStash was founded by two students at the University of Birmingham who wanted to make studying simpler, smarter, and more personal. Balancing lectures, coursework, and revision, they saw first-hand how disconnected digital tools often were from the real learning experience."
      },
      {
        type: "paragraph",
        text: "Their solution was to create a platform that thinks like a student – one that uses AI to make learning more efficient, adaptive, and stress-free. What started as a university side-project has since evolved into a growing education technology company, now partnering with universities across the UK, Europe, and the US."
      },
      {
        type: "heading",
        level: 2,
        text: "Responsible innovation"
      },
      {
        type: "quote",
        text: "Our aim has always been to make AI in education transparent, ethical, and genuinely useful to both students and faculty. Partnering with St Mary's, a university known for its strong community values, is the perfect setting to see how StudyStash can enhance outcomes that matter",
        author: "Ben Ward",
        title: "Co-Founder & CEO, StudyStash",
        image: "/team/ben.jpeg",
        logo: "/favicon-32x32.png"
      },
      {
        type: "paragraph",
        text: "The pilot will run throughout the academic year, gathering insights on student engagement, confidence, and academic outcomes. These findings will inform future iterations of the StudyStash platform as it continues expanding to support higher education institutions worldwide."
      },
      {
        type: "paragraph",
        text: "We're excited to see how our student-focused platform can support online learners and complement the outstanding teaching and innovation happening at St Mary's."
      },
      {
        type: "heading",
        level: 2,
        text: "About StudyStash"
      },
      {
        type: "paragraph",
        text: "StudyStash is a UK-based EdTech company that uses AI and cognitive science to make studying more effective, inclusive, and personalised. The platform automatically generates study tools from course materials in 121 languages, helping students stay motivated and organised, while ensuring all AI use remains responsible, secure, and aligned with institutional standards."
      }
    ],
    
    seo: {
      title: "St Mary's University, Twickenham joins forces with StudyStash | StudyStash",
      description: "St Mary's University partners with StudyStash to pilot AI-powered adaptive learning, supporting student attainment, engagement, and retention through responsible AI.",
      canonical: "https://studystash.com/about/case-studies/st-marys-university"
    },
    
    relatedContent: ["loughborough-university", "university-of-birmingham", "university-of-texas-at-dallas"]
  },

  "university-of-oulu": {
    id: "university-of-oulu",
    type: "case-study",
    category: "partnership",
    title: "University of Oulu partners on large-scale AI initiative",
    description: "The University of Oulu is partnering with StudyStash, an AI-powered study platform designed to help students learn more efficiently, support faculty and improve institutional outcomes.",
    slug: "university-of-oulu",
    publishDate: "2025-01-15",
    readingTime: "4 min read",
    author: {
      name: "Joanna Williams",
      image: "/team/joanna_williams.jpeg"
    },
    hero: {
      image: "/uni-logos/oulu university.jpg",
      imageAlt: "University of Oulu"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "The University of Oulu and StudyStash to bring innovation-first edtech to Finland"
      },
      {
        type: "paragraph",
        text: "The University of Oulu is partnering with StudyStash, an AI-powered study platform designed to help students learn more efficiently, support faculty and improve institutional outcomes. The new pilot will roll out across their business school, initially providing over 2,000+ students access to personalised, adaptive study tools."
      },
      {
        type: "quote",
        text: "The University of Oulu recognises that the responsible use of AI in higher education will become increasingly common in the future. Our partnership with StudyStash provides an opportunity to enhance course accessibility and reduce study-related stress, allowing students to customise materials to better suit their needs and learning behaviour.",
        author: "Kauko Keskisärkkä",
        title: "Director of the Learning Support and Wellbeing Centre, The University of Oulu",
        image: "/Kauko_oulu.png",
        logo: "/uni-logos/uni_oulu_logo_circle.png"
      },
      {
        type: "paragraph",
        text: "StudyStash transforms lecture notes, slides, readings, and module content into cognitive neuroscience grounded revision tool. Features include quizzes, flashcards, summaries, and StudyChat a 24/7 AI learning tutor which are tailored to each student's level and learning habits – all underpinned by ethical and responsible AI."
      },
      {
        type: "heading",
        level: 2,
        text: "Intelligent learning, improved outcomes"
      },
      {
        type: "paragraph",
        text: "Together, we will evaluate how AI-powered tools can boost student engagement, retention, and wellbeing, whilst reducing academic workload. One innovative feature of this collaboration will be splitting the learning between Finnish and English language 50/50 across the student participants."
      },
      {
        type: "paragraph",
        text: "By using StudyStash with Moodle content students will be able to move seamlessly from course materials to study resources without needing additional tools, offering on-the-go learning anywhere, at any time. Feedback from other universities suggests this infrastructure plays a meaningful role in helping students stay engaged and progress successfully through their studies."
      },
      {
        type: "quote",
        text: "As the Digital Pedagogy Mentor and Designer of AI solutions supporting teaching at the University of Oulu, this pilot with StudyStash finally answers the question of how we can give the keys of customisation of learning also in the students' hands in digital learning platforms. I cannot wait to see the enhancement in learning with this.",
        author: "Joakim Tervonen",
        title: "Digital Pedagogist and AI Designer, The University of Oulu",
        image: "/Jokke_oulu.png",
        logo: "/uni-logos/uni_oulu_logo_circle.png"
      },
      {
        type: "heading",
        level: 2,
        text: "A product of student experience"
      },
      {
        type: "paragraph",
        text: "StudyStash was created by students for students. Our founders, whilst studying at the University of Birmingham, saw how disjointed study tools often were from real learning and how ineffective traditional revision offerings were. So they built a platform that adapts to the way students learn, rather than asking students to adapt to the technology, which allows students to retain knowledge through revision."
      },
      {
        type: "paragraph",
        text: "What started as a homegrown solution has since developed into an international EdTech initiative, with universities across the UK, US and now Europe using StudyStash to support learning, organisation, and academic confidence."
      },
      {
        type: "heading",
        level: 2,
        text: "Insight through innovation"
      },
      {
        type: "paragraph",
        text: "StudyStash and Oulu University share a common mission to push forward technical innovations that provide meaningful improvements to education. The pilot will explore how adaptive learning can streamline cognitive load, personalise study pathways, and foster better learning outcomes."
      },
      {
        type: "quote",
        text: "StudyStash empowers our educators by providing course-guiding feedback into student learning progress. By leveraging these valuable insights, our educators can refine their teaching strategies, ensuring students achieve the course's learning objectives with greater success.",
        author: "Kauko Keskisärkkä",
        title: "Director of the Learning Support and Wellbeing Centre, The University of Oulu",
        image: "/Kauko_oulu.png",
        logo: "/uni-logos/uni_oulu_logo_circle.png"
      },
      {
        type: "statistics",
        stats: [
          {
            title: "Globally renowned",
            value: "Top 3% worldwide",
            description: "",
            color: "blue"
          },
          {
            title: "Current students",
            value: "14,500+",
            description: "",
            color: "green"
          },
          {
            title: "Innovative tech investment",
            value: "€2.3million",
            description: "",
            color: "purple"
          }
        ],
        layout: "grid"
      },
      {
        type: "quote",
        text: "Oulu university has a reputation for its commitment to wellbeing, innovation and knowledge, which aligns closely with our mission, making this partnership really exciting. We're looking forward to working together to provide meaningful academic support.",
        author: "Ben Ward",
        title: "Co-Founder & CEO, StudyStash",
        image: "/team/ben.jpeg",
        logo: "/favicon-32x32.png"
      },
      {
        type: "paragraph",
        text: "The pilot will run through the academic year starting in early 2026, assessing engagement, student experiences and outcomes. Insights will shape the platform's development and applications in the European higher education sector."
      },
      {
        type: "heading",
        level: 2,
        text: "About StudyStash"
      },
      {
        type: "paragraph",
        text: "StudyStash is a UK-based EdTech company that uses AI and cognitive science to make studying more personalised, inclusive, and effective.The platform automatically generates study tools from course materials in 121 languages, helping students stay motivated and organised, while ensuring all AI use remains responsible, secure, and aligned with institutional standards."
      }
    ],
    
    seo: {
      title: "University of Oulu partners with StudyStash to pilot innovation-first edtech in Finland | StudyStash",
      description: "The University of Oulu is partnering with StudyStash, an AI-powered study platform designed to help students learn more efficiently, support faculty and improve institutional outcomes.",
      canonical: "https://studystash.com/about/case-studies/university-of-oulu"
    },
    
    relatedContent: ["st-marys-university", "loughborough-university", "university-of-birmingham"]
  },

  "loughborough-university": {
    id: "loughborough-university",
    type: "case-study",
    category: "partnership",
    title: "Loughborough University",
    description: "Loughborough University launches innovative AI-powered teaching-learning pilot with StudyStash, involving 2,000 students across architecture and social sciences.",
    slug: "loughborough-university",
    publishDate: "2025-10-24",
    readingTime: "5 min read",
    author: {
      name: "Joanna Williams",
      image: "/team/joanna_williams.jpeg"
    },
    hero: {
      image: "/case-studies/loughborough_campus.jpg",
      imageAlt: "Loughborough University Campus"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "Loughborough University launches innovative AI-powered teaching-learning pilot in partnership with StudyStash"
      },
      {
        type: "paragraph",
        text: "Loughborough University has announced a partnership with StudyStash, the AI-powered learning platform transforming how students study and engage with their courses at university."
      },
      {
        type: "paragraph",
        text: "The pilot will involve 2,000 students across the School of Architecture, Building and Civil Engineering and the School of Social Sciences and Humanities marking a significant step in Loughborough's exploration of how AI can enhance learning outcomes while supporting academic sustainability."
      },
      {
        type: "quote",
        text: "The pilot is an exciting initiative involving over 2,000 of our students. It reflects our commitment to using safe AI tools that enhance the student experience, while providing valuable support for both our learners and their lecturers at Loughborough.",
        author: "Sam Grogan",
        title: "Pro-Vice-Chancellor of Education and Student Experience",
        image: "/sam-grogan.jpg",
        logo: "/loughborough_crest.png"
      },
      {
        type: "heading",
        level: 2,
        text: "Built by students, for students"
      },
      {
        type: "paragraph",
        text: "StudyStash was founded by University of Birmingham graduates Ben Ward and Jonathan Graham through the university's Elevate incubator, after creating the first version of the platform while revising for their Computer Engineering exams. What began as a student side project has evolved into a promising EdTech innovation, supporting thousands of learners across universities in the UK, Europe, and the United States."
      },
      {
        type: "quote",
        text: "We built StudyStash to solve our own learning challenges – and it worked. We're thrilled to see institutions embracing GAI in their teaching, helping students build familiarity with tools they'll undoubtedly use in the workforce.",
        author: "Ben Ward",
        title: "CEO and Co-Founder",
        image: "/team/ben.jpeg",
        logo: "/favicon-32x32.png"
      },
      {
        type: "heading",
        level: 2,
        text: "Targeted transformation"
      },
      {
        type: "paragraph",
        text: "The pilot will evaluate how StudyStash's AI and neuroscience-informed tools can boost student engagement, retention, and wellbeing - without increasing academic workload."
      },
      {
        type: "paragraph",
        text: "The platform can be used in class or at home, automatically generating adaptive flashcards, quizzes, and study schedules from existing course materials on Moodle. It also features a built-in AI learning assistant, StudyChat, which supports learners in 121 languages-saving faculty time and providing both piloting departments with real-time insights into student progress and challenges."
      },
      {
        type: "heading",
        level: 2,
        text: "Evidence-backed impact"
      },
      {
        type: "paragraph",
        text: "StudyStash is also being deployed across several institutions in the UK. Notably, a large-scale pilot at the University of Birmingham involving over 5,000 students showed:"
      },
      {
        type: "statistics",
        stats: [
          {
            title: "Performance",
            value: "59%",
            description: "reported improved academic performance",
            color: "blue"
          },
          {
            title: "Confidence",
            value: "74%",
            description: "felt more confident managing their workload",
            color: "green"
          },
          {
            title: "Wellbeing",
            value: "83%",
            description: "experienced reduced stress levels",
            color: "purple"
          }
        ],
        layout: "grid"
      },
      {
        type: "quote",
        text: "There's a potentially huge benefit for lecturers – giving them more time to focus on students who need one-to-one support. This is a real game-changer for the academic teaching community.",
        author: "Jonathan Graham",
        title: "CTO and Co-Founder",
        image: "/team/jonathan.png",
        logo: "/favicon-32x32.png"
      },
      {
        type: "heading",
        level: 2,
        text: "Looking ahead"
      },
      {
        type: "paragraph",
        text: "Following the Loughborough pilot, StudyStash plans further collaborations with universities across the UK, US, Europe, and South Africa – empowering institutions to deliver ethical, accessible, and effective AI learning experiences."
      },
      {
        type: "quote",
        text: "This partnership with Loughborough marks a major step forward. Together, we're demonstrating how AI can be a force for good in education, improving outcomes for students while supporting the academics who teach them.",
        author: "Alan Teece",
        title: "CMO StudyStash",
        image: "/team/alan.jpg",
        logo: "/favicon-32x32.png"
      },
      {
        type: "heading",
        level: 2,
        text: "About StudyStash"
      },
      {
        type: "paragraph",
        text: "StudyStash is an AI- and neuroscience-informed learning platform built by University of Birmingham graduates Ben Ward and Jonathan Graham. It personalises study materials, boosts engagement, and provides real-time insights for educators, all while protecting data privacy and academic integrity."
      }
    ],
    
    seo: {
      title: "Loughborough University Case Study | StudyStash",
      description: "Loughborough University launches innovative AI-powered teaching-learning pilot with StudyStash, involving 2,000 students to enhance learning outcomes.",
      canonical: "https://studystash.com/about/case-studies/loughborough-university"
    },
    
    relatedContent: ["university-of-birmingham", "university-of-texas-at-dallas", "coventry-university"]
  },

  "university-of-texas-at-dallas": {
    id: "university-of-texas-at-dallas",
    type: "case-study",
    category: "partnership",
    title: "University of Texas at Dallas",
    description: "StudyStash collaborated with the University of Texas at Dallas to explore how active recall and smarter study tools can boost learning outcomes for entrepreneurship students.",
    slug: "university-of-texas-at-dallas",
    publishDate: "2024-12-10",
    readingTime: "4 min read",
    author: {
      name: "Joanna Williams",
      image: "/team/joanna_williams.jpeg"
    },
    hero: {
      image: "/case-studies/utd.jpg",
      imageAlt: "University of Texas at Dallas Business School"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "University of Texas at Dallas Case Study"
      },
      {
        type: "paragraph",
        text: "StudyStash recently partnered with the University of Texas at Dallas (UT Dallas) Business School to introduce innovative AI-powered study tools to approximately 200 entrepreneurship students, demonstrating the real-world impact of technology in education."
      },
      {
        type: "heading",
        level: 2,
        text: "College Collaboration"
      },
      {
        type: "paragraph",
        text: "In their entrepreneurship classes, students at UT Dallas Business School explored how active recall and personalised study tools can strengthen learning. At the same time, they were given a behind-the-scenes look at how StudyStash itself was created and scaled."
      },
      {
        type: "paragraph",
        text: "It was a fantastic opportunity to demonstrate the practical side of technology in education, as well as helping students feel more organised, confident, and better prepared for the challenges of modern learning."
      },
      {
        type: "heading",
        level: 2,
        text: "Student Learning Outcomes"
      },
      {
        type: "paragraph",
        text: "Students experienced firsthand how StudyStash's AI-powered tools can transform their study routines. They saw how personalised materials, progress tracking, and active recall strategies not only make learning more efficient and engaging but also provide the organisational support busy students need."
      },
      {
        type: "paragraph",
        text: "The entrepreneurship cohort responded particularly well to the idea of technology solving everyday study challenges, seeing clear connections between our tools and their own academic needs."
      },
      {
        type: "heading",
        level: 2,
        text: "Entrepreneurship Education"
      },
      {
        type: "paragraph",
        text: "Beyond the platform's study benefits, students were able to gain firsthand insight into the entrepreneurial journey behind StudyStash, from the first idea to scaling across universities and colleges across the globe. This perspective added depth to their entrepreneurship education, offering practical examples of innovation, problem-solving, and sustainable business growth in action."
      },
      {
        type: "quote",
        text: "The session was a chance to show the real-world impact of technology in education-helping students stay organised, confident, and ready for the challenges ahead.",
        author: "Jonathan Graham",
        title: "Chief Technology Officer",
        image: "/team/jonathan.png",
        logo: "/favicon-32x32.png"
      },
      {
        type: "heading",
        level: 2,
        text: "Future Collaboration"
      },
      {
        type: "paragraph",
        text: "The successful launch at UT Dallas Business School opens the door for ongoing collaboration; by combining powerful study tools with real-world startup insights, StudyStash can continue to enrich both student learning and entrepreneurship education."
      },
      {
        type: "paragraph",
        text: "This collaboration represents StudyStash's commitment to supporting diverse educational programs and demonstrating the value of AI-powered learning across different academic disciplines and student populations."
      }
    ],
    
    seo: {
      title: "University of Texas at Dallas Case Study | StudyStash",
      description: "StudyStash partners with the University of Texas at Dallas to explore how active recall and smarter study tools can boost learning outcomes for entrepreneurship students.",
      canonical: "https://studystash.com/about/case-studies/university-of-texas-at-dallas"
    },
    
    relatedContent: ["university-of-birmingham", "coventry-university", "9-to-thrive-podcast"]
  },

  "science-behind-smarter-learning": {
    id: "science-behind-smarter-learning",
    type: "case-study",
    category: "news",
    title: "The Science Behind Smarter Learning",
    description: "Students today have more tools at their fingertips than ever before, but are they using them effectively? Discover how our neuroscience-backed approach helps to turn digital learning into real progress.",
    slug: "science-behind-smarter-learning",
    publishDate: "2025-09-02",
    readingTime: "3 min read",
    author: {
      name: "Jonathan Graham",
      image: "/team/jonathan.png"
    },
    hero: {
      image: "/student-teacher.jpg",
      imageAlt: "Visual representation of personalized learning at scale"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "The Science Behind Smarter Learning"
      },
      {
        type: "paragraph",
        text: "In today's digital age, students have access to an ever growing number of learning tools. However, with endless PDFs, unstructured lecture recordings, and content that often feels overwhelming, the real challenge isn't access - it's effectiveness. The question that education providers ask remains: can digital learning translate into better outcomes, stronger retention, and higher student satisfaction?"
      },
      {
        type: "heading",
        level: 2,
        text: "What makes StudyStash different?"
      },
      {
        type: "paragraph",
        text: "We drive retention, not just learning: where most platforms stop at educational outcomes, we go further by linking learning intelligence directly to retention and satisfaction metrics that matter for institutional success."
      },
      {
        type: "paragraph",
        text: "We're proven, not experimental: grounded in neuroscience research, our methodology is tried and trusted. Universities can't afford to gamble on unproven solutions, which is why we offer a credible, evidence-based approach."
      },
      {
        type: "paragraph",
        text: "We simplify, not complicate: StudyStash completes the learning loop by integrating seamlessly into your existing systems. No lecturer training, no workflow changes, just measurable impact from day one."
      },
      {
        type: "heading",
        level: 2,
        text: "We speak the language of universities"
      },
      {
        type: "paragraph",
        text: "Unlike generic AI tools, we're built for higher education – from module structures and assessment periods to the complexities of progression, we truly understand your academic environment."
      },
      {
        type: "paragraph",
        text: "StudyStash delivers intelligence that drives action; students receive personalised learning journeys while lecturers gain weekly insights into exactly where students struggle. This isn't just data, it's actionable intelligence that improves teaching without adding workload."
      },
      {
        type: "heading",
        level: 2,
        text: "Endorsed by educators"
      },
      {
        type: "quote",
        text: "What I really like about StudyStash is that it simplifies the revision process, while still drawing on the neuroscience of learning.",
        author: "Benjamin Ellis",
        title: "Head of Education (School of Sport, Exercise and Rehabilitation Services), University of Birmingham",
        image: "/quote_images/benjamin_ellis.jpg",
        logo: "/uob_crest.png"
      },
      {
        type: "heading",
        level: 2,
        text: "Built by students, for students"
      },
      {
        type: "paragraph",
        text: "StudyStash was born out of lived experience (and frustration). As undergraduates, our founders Ben and Jonathan faced the same content overwhelm that millions of students still experience today: hundreds of pages of notes, lecture recordings without structure, and materials that worked for lecturers but not necessarily learners."
      },
      {
        type: "paragraph",
        text: "This student perspective became their strategic advantage. They weren't researching problems from boardrooms, they lived the challenge in lecture halls. They understood the gap between how universities deliver content and how students actually learn, because they'd felt it themselves."
      },
      {
        type: "statistics",
        stats: [
          {
            value: "59%",
            title: "Student Attainment",
            description: "Of students reported StudyStash helped improve their academic performance",
            color: "blue"
          },
          {
            value: "74%",
            title: "Student Confidence",
            description: "Of students reported increased confidence managing study workload",
            color: "green"
          },
          {
            value: "34%",
            title: "Student Engagement",
            description: "Of students regularly used StudyStash outside of lecture time (avg. 3 hours 20 mins/week)",
            color: "purple"
          }
        ],
        layout: "grid"
      },
      {
        type: "heading",
        level: 2,
        text: "The smarter way to student success"
      },
      {
        type: "paragraph",
        text: "Our inside-out approach allowed us to unlock insights missed by traditional software: that students learn best when they can interrogate content in their own language, that spaced repetition only works when it fits real schedules, and that gamification must lead to real progress, not just points."
      },
      {
        type: "paragraph",
        text: "Today, StudyStash combines the authentic student perspective with rigorous neuroscience research. We're close enough to understand the problem, smart enough to solve it scientifically, and experienced enough to deliver results at scale."
      },
      {
        type: "paragraph",
        text: "Do you want to join the universities achieving improved learning, stronger retention, and a reputation for delivering the best possible student experience? Book a demo today."
      }
    ],
    
    seo: {
      title: "The Science Behind Smarter Learning | StudyStash",
      description: "Students today have more tools at their fingertips than ever before, but are they using them effectively? Discover how our neuroscience-backed approach helps to turn digital learning into real progress.",
      canonical: "https://studystash.com/about/case-studies/science-behind-smarter-learning"
    },
    
    relatedContent: ["university-of-birmingham", "university-of-texas-at-dallas", "coventry-university"]
  },

  "ai-ethics-in-he": {
    id: "ai-ethics-in-he",
    type: "case-study",
    category: "news",
    title: "Is AI adoption in HE leaving ethics behind?",
    description: "AI is transforming the academic space, from the way students learn to how educators teach, but without ethics at its center, even the most innovative tools can lose sight of their purpose.",
    slug: "ai-ethics-in-he",
    publishDate: "2025-10-24",
    readingTime: "5 min read",
    author: {
      name: "Ben Ward",
      image: "/team/ben.jpeg"
    },
    hero: {
      image: "/ethics_ai_img.png",
      imageAlt: "AI Ethics in Higher Education"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "Is AI adoption in HE leaving ethics behind?"
      },
      {
        type: "paragraph",
        text: "AI is transforming the academic space, from the way students learn to how educators teach, but without ethics at its center, even the most innovative tools can lose sight of their purpose."
      },
      {
        type: "paragraph",
        text: "At StudyStash, ethics are at the heart of our design. Echoing the EDUCAUSE AI Ethical Guidelines, and the Association for Learning Technology's (ALT) focus on agency, transparency, inclusion, and accountability, our platform was built around values that protect both educators and learners."
      },
      {
        type: "heading",
        level: 2,
        text: "Ethics first, always"
      },
      {
        type: "paragraph",
        text: "According to a study presented by the Higher Education Policy Institute, students are concerned with using AI-powered edtech. 53% reported worries of cheating accusations, and 51% stated that they were put-off from using AI due to false results, or hallucinations."
      },
      {
        type: "paragraph",
        text: "The same research showed that only 36% of these undergraduates using AI had been taught skills to use these generative platforms by their institutions."
      },
      {
        type: "paragraph",
        text: "With generative AI transforming the education sector, amid budget cuts and stretched faculties, the key to safe and responsible outcomes for students and institutions using AI is built-in ethics and guardrails."
      },
      {
        type: "heading",
        level: 2,
        text: "How we embed ethical guardrails into every feature"
      },
      {
        type: "paragraph",
        text: "Transparency always: AI-generated content is labeled with a sparkle emoji, and a 'Why did this appear?' buttons explain how AI found answers in your course materials."
      },
      {
        type: "paragraph",
        text: "Autonomy guarded: Lecturers are safe in the knowledge that their intellectual property is just that - theirs. We will never use uploaded documents or data to train our AI models."
      },
      {
        type: "paragraph",
        text: "Fairness upheld: StudyStash meets WCAG 2.2 AA accessibility standards, ensuring an inclusive experience for all students, including those with disabilities."
      },
      {
        type: "paragraph",
        text: "Accountability guaranteed: We subject our AI implementation to quarterly reviews, with multi-stakeholder input including our AI safety team, external advisors and user feedback."
      },
      {
        type: "paragraph",
        text: "Privacy protection: StudyStash follows strict data security and privacy standards. We comply with GDPR, UK and US data protection law."
      },
      {
        type: "paragraph",
        text: "Risk evaluation: We pilot every new feature on a small scale, document its potential impacts, and act quickly to mitigate any issues."
      },
      {
        type: "quote",
        text: "Privacy enables depth. It allows students to engage fully, think freely, and learn meaningfully. Without this, the result is shallow usage in the short term, and a skills deficit in the long term.",
        author: "Tom Moule",
        title: "TechUK and Jisc",
        image: "/Tom_Moule.jpg",
        logo: "/jisc.png"
      },
      {
        type: "heading",
        level: 2,
        text: "The reality of unreadiness"
      },
      {
        type: "paragraph",
        text: "Recent surveys reveal that over 60% of UK universities have yet to introduce formal AI usage policies for staff or students. This lack of governance has led to confusion and inconsistency in how AI is adopted across institutions. While some departments experiment with new tools, others remain cautious often due to ethical uncertainty and data privacy risks."
      },
      {
        type: "paragraph",
        text: "In practice, this means that while students are already using AI to study, many educators feel underprepared to manage it. Jisc's Student Perceptions of AI Report found that more than half of students worry about how AI use could affect perceptions of originality and fairness, and 48% believe their lecturers don't fully understand how generative AI works."
      },
      {
        type: "paragraph",
        text: "The challenge isn't a lack of innovation, it's a lack of ethical readiness. Without transparency, students lose trust. Without clear guidance, lecturers lose confidence. And without accountability, institutions risk reputational damage when AI goes wrong."
      },
      {
        type: "heading",
        level: 2,
        text: "Looking to the future"
      },
      {
        type: "paragraph",
        text: "As higher education accelerates towards digital transformation, ethical AI governance must move from aspiration to infrastructure. That means frameworks for explainability, clear consent around data use, and student inclusion in how AI is deployed in their learning environments."
      },
      {
        type: "paragraph",
        text: "Organisations such as TechUK, ALT, and the OECD continue to call for stronger ethical alignment across EdTech, emphasising that AI's success in education will depend not on speed, but on trust. The future will belong to universities that treat ethical AI not as a compliance exercise, but as a competitive advantage by embedding transparency, fairness, and accountability into every layer of their learning systems."
      },
      {
        type: "quote",
        text: "Greater transparency is key to building trust in artificial intelligence and accelerating its adoption.",
        author: "Jerry Sheehan",
        title: "OECD",
        image: "/Jerry Sheehan.jpeg",
        logo: "/oecd.jpg"
      },
      {
        type: "paragraph",
        text: "For universities, that trust begins not with what AI can do, but with how responsibly it is built, taught, and governed."
      },
      {
        type: "paragraph",
        text: "Ethical AI in education is not optional, it's essential. Our tools empower students to learn confidently, with ethical guardrails that keep their studies grounded in their lecturers' materials. It's adaptive technology designed to transform students' learning on their terms."
      },
      {
        type: "paragraph",
        text: "If you're interested in learning more about our approach to adaptive AI-generated learning, you can book a demo with a member of our team."
      },
      {
        type: "cta-button",
        text: "Book a demo",
        href: "https://studystash.com/book-a-demo"
      },
      {
        type: "sources",
        title: "Sources & References",
        items: [
          {
            id: "techuk-ai-privacy",
            title: "Why access to AI privacy must be part of the digital inclusion conversation",
            authors: ["Tom Moule"],
            date: "2025",
            url: "https://www.techuk.org/resource/why-access-to-ai-privacy-must-be-part-of-the-digital-inclusion-conversation.html",
            type: "article",
            publication: "TechUK",
            description: "Explores the intersection of AI privacy, digital inclusion, and how privacy enables meaningful student engagement with educational technology."
          },
          {
            id: "uuk-transformation-efficiency",
            title: "Transformation and Efficiency: Towards a new era of collaboration",
            authors: ["Universities UK"],
            date: "May 2025",
            url: "https://www.universitiesuk.ac.uk/sites/default/files/field/downloads/2025-05/UUK-Transformation-and-Efficiency-Towards-a-new-era-of-collaboration-2025_1.pdf",
            type: "article",
            publication: "Universities UK",
            description: "A comprehensive report on digital transformation in UK higher education, examining efficiency challenges and the role of technology in institutional collaboration."
          },
          {
            id: "hepi-ai-survey-2025",
            title: "Student Generative AI Survey 2025",
            authors: ["Higher Education Policy Institute"],
            date: "2025",
            url: "https://www.hepi.ac.uk/reports/student-generative-ai-survey-2025",
            type: "article",
            publication: "HEPI",
            description: "Research revealing that 53% of students worry about cheating accusations when using AI, and 51% are concerned about AI hallucinations, with only 36% receiving institutional training on AI use."
          },
          {
            id: "jisc-student-perceptions-2025",
            title: "Student Perceptions of AI 2025",
            authors: ["Jisc"],
            date: "2025",
            url: "https://www.jisc.ac.uk/reports/student-perceptions-of-ai-2025",
            type: "article",
            publication: "Jisc",
            description: "Survey findings showing that over half of students worry about how AI affects perceptions of originality and fairness, with 48% believing their lecturers don't fully understand generative AI."
          },
          {
            id: "oecd-transparency-ai",
            title: "OECD finds growing transparency efforts among leading AI developers",
            authors: ["Jerry Sheehan", "OECD"],
            date: "September 2025",
            url: "https://www.oecd.org/en/about/news/press-releases/2025/09/oecd-finds-growing-transparency-efforts-among-leading-ai-developers.html",
            type: "article",
            publication: "OECD",
            description: "Press release highlighting the importance of transparency in building trust in artificial intelligence and accelerating its responsible adoption across sectors including education."
          }
        ]
      }
    ],
    
    seo: {
      title: "Is AI adoption in HE leaving ethics behind? | StudyStash",
      description: "AI is transforming the academic space, from the way students learn to how educators teach, but without ethics at its center, even the most innovative tools can lose sight of their purpose.",
      canonical: "https://studystash.com/about/case-studies/ai-ethics-in-he"
    },
    
    relatedContent: ["science-behind-smarter-learning", "university-of-birmingham", "eab-partnership"]
  },

  "studystash-funding-round": {
    id: "studystash-funding-round",
    type: "case-study",
    category: "news",
    title: "StudyStash Closes Pre-Seed Funding Round to Advance Student-Centred Learning",
    description: "StudyStash, a student-founded EdTech company using AI to make studying more effective and inclusive, has officially closed its pre-seed venture round.",
    slug: "studystash-funding-round",
    publishDate: "2025-01-20",
    readingTime: "4 min read",
    author: {
      name: "Joanna Williams",
      image: "/team/joanna_williams.jpeg"
    },
    hero: {
      image: "/team/Ben and Jonathan.jpg",
      imageAlt: "StudyStash team celebrating pre-seed funding"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "StudyStash Closes Pre-Seed Funding Round to Advance Student-Centred Learning"
      },
      {
        type: "paragraph",
        text: "StudyStash, a student-founded EdTech company using AI to make studying more effective and inclusive, has officially closed its pre-seed venture round."
      },
      {
        type: "paragraph",
        text: "Founded by university friends Ben Ward and Jonathan Graham while studying at the University of Birmingham, StudyStash was created out of a shared frustration with the lack of practical, personalised study tools available to students. What began as an idea in their student house has grown into a fast-moving education technology company working with universities across the UK, Europe, the US, and South Africa."
      },
      {
        type: "quote",
        text: "As the CEO of StudyStash, I am delighted to announce the close of our pre-seed venture round. It feels like just yesterday that Jonathan Graham and I were sitting in our university house with the idea. Now it's real.",
        author: "Ben Ward",
        title: "Co-Founder and CEO, StudyStash",
        image: "/team/ben.jpeg",
        logo: "/favicon-32x32.png"
      },
      {
        type: "heading",
        level: 2,
        text: "Building on Proven Success"
      },
      {
        type: "paragraph",
        text: "Since launching, StudyStash has partnered with leading higher education institutions to explore how AI can help improve student engagement, boost completion rates, and reduce academic workload pressures."
      },
      {
        type: "heading",
        level: 2,
        text: "Expanding Global Reach"
      },
      {
        type: "paragraph",
        text: "The newly completed pre-seed round will enable the business to expand its university partnerships and enhance the platform's adaptive learning features, ensuring it continues to meet the diverse needs of learners worldwide."
      },
      {
        type: "quote",
        text: "The whole team is working exceptionally hard to bring exceptional teaching and learning technology to universities across the UK, Europe, the US, and even now South Africa.",
        author: "Ben Ward",
        title: "Co-Founder and CEO, StudyStash",
        image: "/team/ben.jpeg",
        logo: "/favicon-32x32.png"
      },
      {
        type: "heading",
        level: 2,
        text: "Looking Ahead"
      },
      {
        type: "paragraph",
        text: "This momentum will support the business to deepen its research collaborations and scale its reach to support more institutions focused on improving the student experience."
      },
      {
        type: "paragraph",
        text: "A huge thank you to the angels who believed in our mission - and in us - from day one."
      },
      {
        type: "heading",
        level: 2,
        text: "Interested in Learning More?"
      },
      {
        type: "paragraph",
        text: "Interested in learning how the platform supports student engagement, completion rates, and faculty workload?"
      },
      {
        type: "cta-button",
        text: "Learn more",
        href: "https://studystash.com/"
      }
    ],
    
    seo: {
      title: "StudyStash Closes Pre-Seed Funding Round | StudyStash",
      description: "StudyStash, a student-founded EdTech company using AI to make studying more effective and inclusive, has officially closed its pre-seed venture round.",
      canonical: "https://studystash.com/about/case-studies/studystash-funding-round"
    },
    
    relatedContent: ["university-of-birmingham", "eab-partnership", "loughborough-university"]
  },
  "bowling-green-state-university": {
    id: "bowling-green-state-university",
    type: "case-study",
    category: "partnership",
    title: "Bowling Green State University Ohio (BGSU) partners with StudyStash on a new AI study initiative",
    description: "Bowling Green State University Ohio (BGSU) is launching a new partnership with StudyStash, an AI-powered learning platform as part of its strategy to boost student engagement and deploy AI safely within its learning environment, Canvas.",
    slug: "bowling-green-state-university",
    publishDate: "2026-02-02",
    readingTime: "4 min read",
    author: {
      name: "Joanna Williams",
      image: "/team/joanna_williams.jpeg"
    },
    hero: {
      image: "/uni-logos/bgsu_campus.jpg",
      imageAlt: "Bowling Green State University Logo"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "Bowling Green State University partners with StudyStash on a new AI study initiative"
      },
      {
        type: "paragraph",
        text: "Bowling Green State University (BGSU) in Ohio is launching a new partnership with StudyStash, an AI-powered learning platform as part of its strategy to boost student engagement and deploy AI safely within its learning environment, Canvas."
      },
      {
        type: "quote",
        text: "Partnering with StudyStash demonstrates BGSU's firm commitment to empowering students with the strategies and tools – and a deep sense of curiosity – needed to ensure they get the most possible out of their education.",
        author: "Glenn Davis",
        title: "Vice President, Student Engagement and Success",
        image: "/uni-logos/glenn_davis.jpg",
        logo: "/uni-logos/bgsu.png"
      },
      {
        type: "paragraph",
        text: "The collaboration will focus on how adaptive study tools can fit into students' existing academic routines, helping them make sense of complex course material, revisit key concepts more effectively, and approach assessments with greater confidence."
      },
      {
        type: "paragraph",
        text: "The new pilot will roll out in Physics, Computer Science, and Biology, with undergraduate students participating through BGSU's Learning Management System (LMS)."
      },
      {
        type: "heading",
        level: 2,
        text: "A Shared Commitment to Student Success"
      },
      {
        type: "paragraph",
        text: "Bowling Green State University, ranked <a href=\"https://www.bgsu.edu/news/online-media-newsroom/2025/10/bgsu-ranked-first-in-ohio-among-the-top-in-the-nation-as-a-university-students-say-they-would-choose-again-according-to-wall-street-journal.html\" target=\"_blank\" rel=\"noopener noreferrer\" class=\"text-blue-600 hover:text-blue-800 underline\">#1 public university in Ohio for student experience</a> by The Wall Street Journal, promises learning that serves the public good. This partnership explores how adaptive study tools can strengthen independent learning, confidence, and engagement within existing course structures. By embedding StudyStash directly into course workflows, the pilot supports students without added complexity while giving instructors clearer insight into how learners engage with course material."
      },
      {
        type: "paragraph",
        text: "StudyStash's adaptive approach aligns with BGSU's commitment to inclusive and ethical education, meeting students where they are and adjusting support as learning develops. Built with guardrails around academic integrity, data privacy, and transparency, the platform is designed to complement teaching by reducing repetitive workload and enabling timely academic support."
      },
      {
        type: "heading",
        level: 2,
        text: "A student-centered solution"
      },
      {
        type: "paragraph",
        text: "StudyStash was created by students, for students. Founded by University of Birmingham graduates Ben Ward and Jonathan Graham, who created the first version of the platform while revising for their Computer Engineering exams. They built StudyStash to adapt to learners, rather than asking learners to adapt to technology, helping students retain knowledge more effectively through structured, evidence-based revision. What began as a student-built solution has since grown into an international EdTech initiative, now supporting institutions across the UK, US, and Europe to improve learning organization, confidence, and outcomes."
      },
      {
        type: "heading",
        level: 2,
        text: "Evidence-based impact"
      },
      {
        type: "paragraph",
        text: "BGSU and StudyStash share an interest in using technology thoughtfully, testing ideas, measuring impact, and learning from real student experiences. Throughout the pilot, the focus will remain on understanding what genuinely helps students learn more effectively, and where AI can add value without compromising trust or academic standards."
      },
      {
        type: "paragraph",
        text: "The pilot will run during the Spring 2026 term, with findings shared internally to support ongoing evaluation and decision-making. Insights from the pilot will inform future development of the platform and contribute to wider conversations around responsible, evidence-based AI adoption in US higher education."
      },
      {
        type: "quote",
        text: "BGSU's commitment to innovation and using technology to better support their students makes them an ideal partner for us. We're excited to collaborate with such a thoughtful and student-focused institution.",
        author: "Ben Ward",
        title: "Co-Founder & CEO, StudyStash",
        image: "/team/ben.jpeg",
        logo: "/favicon-32x32.png"
      },
      {
        type: "heading",
        level: 2,
        text: "About StudyStash"
      },
      {
        type: "paragraph",
        text: "StudyStash is a UK-based EdTech company that uses AI and cognitive science to make studying more personalized, inclusive, and effective. The platform automatically generates adaptive study tools from course materials in 120+ languages, helping students stay organized and confident, all while ensuring all AI use remains secure, ethical, and aligned with institutional standards."
      },
      {
        type: "sources",
        title: "Sources & References",
        items: [
          {
            id: "bgsu-wsj-ranking-2025",
            title: "Six years in a row: BGSU ranks No. 1 in Ohio, among the top 5 in the nation students say they would choose again, according to The Wall Street Journal",
            date: "October 2025",
            url: "https://www.bgsu.edu/news/online-media-newsroom/2025/10/bgsu-ranked-first-in-ohio-among-the-top-in-the-nation-as-a-university-students-say-they-would-choose-again-according-to-wall-street-journal.html",
            type: "article",
            publication: "Bowling Green State University",
            description: "BGSU has been ranked by The Wall Street Journal among the top five universities in the nation for student recommendation, placing No. 1 in Ohio and No. 3 among all public universities for its student-recommendation score."
          }
        ]
      }
    ],
    
    seo: {
      title: "Bowling Green State University Ohio (BGSU) partners with StudyStash on a new AI study initiative | StudyStash",
      description: "Bowling Green State University Ohio (BGSU) is launching a new partnership with StudyStash, an AI-powered learning platform as part of its strategy to boost student engagement and deploy AI safely within its learning environment, Canvas.",
      canonical: "https://studystash.com/about/case-studies/bowling-green-state-university"
    },
    
    relatedContent: ["university-of-texas-at-dallas", "university-of-birmingham", "loughborough-university"]
  },
  "university-of-birmingham-les": {
    id: "university-of-birmingham-les",
    type: "case-study",
    category: "success-story",
    title: "A Lecturer's Take: Piloting AI in HE",
    description: "How to make sure that good Edtech has great outcomes...",
    slug: "university-of-birmingham-les",
    publishDate: "2026-03-06",
    readingTime: "6 min read",
    author: {
      name: "Benjamin Ellis MSc, BSc, MCSP",
      image: "/case-studies/ben-ellis.jpg"
    },
    hero: {
      image: "/case-studies/jisc_digifest_ben_ellis.jpg",
      imageAlt: "Ben Ellis speaking at Jisc Digifest"
    },
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "A Lecturer's Take: Piloting AI in HE"
      },
      {
        type: "paragraph",
        text: "University of Birmingham · College of Life and Environmental Sciences"
      },
      {
        type: "paragraph",
        text: "This perspective outlines how LES embedded StudyStash in teaching practice to support organisation, confidence, and sustainable revision habits across modules."
      },
      {
        type: "statistics",
        stats: [
          {
            value: "58%",
            title: "Pre/post using StudyStash",
            description: "Of students reported increased organisation with their studies",
            color: "blue"
          },
          {
            value: "78%",
            title: "Pre/post using StudyStash",
            description: "Of students reported increased confidence managing study and revision workload",
            color: "blue"
          }
        ],
        layout: "outcome-cards"
      },
      {
        type: "heading",
        level: 2,
        text: "Lecturer Perspective"
      },
      {
        type: "paragraph",
        text: "Ben Ellis is an innovative and forward-thinking physiotherapy lecturer with an interest in active learning approaches that develop critical thinking and evidence-based physiotherapists. His research interests include collaborative and digital educational approaches, simulation, and clinical reasoning."
      },
      {
        type: "quote",
        text: "What I really like about StudyStash is that it simplifies the revision process, but still draws on all the neuroscience of learning.",
        author: "Benjamin Ellis MSc, BSc, MCSP",
        title: "Head of School (Education), School of Sport, Exercise and Rehabilitation Sciences",
        image: "/case-studies/ben-ellis.jpg",
        logo: "/uob_crest.png"
      },
      {
        type: "heading",
        level: 2,
        text: "UoB Pilot Background"
      },
      {
        type: "bullet-list",
        items: [
          "5,000+ students enrolled.",
          "Implementation across all five colleges at UoB.",
          "Initial 16-month pilot from 2023 to 2025."
        ]
      },
      {
        type: "heading",
        level: 2,
        text: "What did we learn?"
      },
      {
        type: "bullet-list",
        items: [
          "A passionate advocate is necessary to embed technology effectively.",
          "Match the technology to the type of learning required.",
          "Introduce tools early in programmes and at the start of modules to establish learning habits.",
          "Students want to learn how to learn, but adopting new technology requires behaviour change intervention."
        ]
      },
      {
        type: "bullet-list",
        items: [
          "Build capability with in-session time.",
          "Explain learning for motivation.",
          "Demonstrate value to students with data, engagement = achievement."
        ],
        style: "subpoints"
      },
      {
        type: "heading",
        level: 2,
        text: "Student Perspective"
      },
      {
        type: "paragraph",
        text: "A student reflection on how StudyStash supported revision and confidence in LES modules."
      },
      {
        type: "video",
        url: USER_TESTIMONIALS.Tedi,
        title: "Tedi's Experience with StudyStash",
        platform: "cloudflare"
      }
    ],
    seo: {
      title: "A Lecturer's Take: Piloting AI in HE | StudyStash",
      description: "Read a lecturer perspective from the University of Birmingham LES pilot and how StudyStash supported measurable gains in organisation and confidence.",
      canonical: "https://studystash.com/about/case-studies/university-of-birmingham/les"
    },
    relatedContent: ["university-of-birmingham", "bowling-green-state-university", "temis-story"]
  },
  "temis-story": {
    id: "temis-story",
    type: "case-study",
    category: "success-story",
    title: "StudyStash Success Story: How Temi Simplified her Study Process",
    description: "How Temi saved time on studying in second year",
    slug: "temis-story",
    publishDate: "2026-01-30",
    readingTime: "5 min read",
    author: {
      name: "Joanna Williams",
      image: "/team/joanna_williams.jpeg"
    },
    hero: {
      image: "/case-studies/temi.jpg",
      imageAlt: "Temi Ajayi, computer science student at the University of Birmingham"
    },
    
    contentBlocks: [
      {
        type: "heading",
        level: 1,
        text: "StudyStash Success Story: How Temi Simplified her Study Process"
      },
      {
        type: "paragraph",
        text: "After being introduced to StudyStash by a lecturer in first year, Temi Ajayi began using the platform for note-taking and flashcards. We caught up to learn more about her experience, where she found it particularly useful, and how it helped her organise her content."
      },
      {
        type: "heading",
        level: 2,
        text: "Temi's Journey to Exam Confidence with StudyStash"
      },
      {
        type: "heading",
        level: 2,
        text: "About Temi"
      },
      {
        type: "paragraph",
        text: "I'm Temi Ajayi and I'm studying computer science at the University of Birmingham. It's going well so far, of course sometimes it is difficult, but isn't education always a bit challenging. I started using StudyStash in year one of my studies after our lecturer introduced it to us as a new platform that you can use to take notes, make flashcards, all sorts."
      },
      {
        type: "heading",
        level: 2,
        text: "Before StudyStash"
      },
      {
        type: "paragraph",
        text: "Before StudyStash I'd generally just take down notes; after a lecture I'd write down flashcards from those notes (in my own words) on different topics, and if there was programming content, I'd just write it down in a different color on good notes."
      },
      {
        type: "heading",
        level: 2,
        text: "Favourite Features"
      },
      {
        type: "paragraph",
        text: "StudyStash flashcards and notes I use a lot, especially notes for programming, notes for good snippets. I also use it for notes on programming; creating a new folder for learning React, for example, or JavaScript for example and then I take down notes for that. I find it very useful for programming notes! I also really like that you can schedule exams and then it tells you how often you should do flashcards. I recently found out that my friends were using the notes and the flash cards together to generate tests which I didn't think of doing so I'll be trying those features."
      },
      {
        type: "heading",
        level: 2,
        text: "After StudyStash"
      },
      {
        type: "paragraph",
        text: "It has helped me become a lot more organized, that's a key thing. It's nice having my flash cards from year one, most of my modules now depend on year one modules, so I can look back at flashcards and notes to help me make notes for year two. The organisational aspect is a great help."
      },
      {
        type: "heading",
        level: 2,
        text: "Final Thoughts"
      },
      {
        type: "paragraph",
        text: "I'd recommend StudyStash to other students because first off, your institution is paying for it, so it's a no brainer. I think the features that it has will surprise you. I haven't been using the test features yet, but I will be using them around exam time or when exams come up. I think that it's a really really really convenient platform and it was helpful for my first year exams."
      }
    ],
    seo: {
      title: "Temi's Success Story: Streamlined Revising with StudyStash",
      description: "How Temi saved time on studying in second year. Temi Ajayi, a computer science student at the University of Birmingham, shares how StudyStash helped her organize her content and streamline her study process.",
      canonical: "https://studystash.com/about/case-studies/temis-story"
    },
    relatedContent: ["jaimees-story", "jaipals-story", "university-of-birmingham"]
  },
  "hannahs-story": {
    id: "hannahs-story",
    type: "case-study",
    category: "success-story",
    title: "How Hannah Perfected Her Study Process",
    description: "Hannah Dickson, a second-year quantity surveying student at Loughborough, shares how StudyStash helped her convert notes into flashcards, save time, and study on the go.",
    slug: "hannahs-story",
    publishDate: "2026-03-12",
    readingTime: "6 min read",
    author: {
      name: "Joanna Williams",
      image: "/team/joanna_williams.jpeg"
    },
    hero: {
      image: "/case-studies/hannah_dickinson.jpeg",
      imageAlt: "Hannah Dickson, second-year quantity surveying student at Loughborough"
    },
    contentBlocks: [
      {
        type: "paragraph",
        text: "Hannah Dickson, a second-year quantity surveying student at Loughborough, started using StudyStash in September. She has found the platform's ability to convert notes into flashcards a game-changer for studying, on-the-go learning, and saving time. Find out more about her experience in this StudyStash Success Story."
      },
      {
        type: "heading",
        level: 2,
        text: "Hannah's trajectory to timesaving revision routines with StudyStash"
      },
      {
        type: "heading",
        level: 2,
        text: "About Hannah"
      },
      {
        type: "paragraph",
        text: "I'm Hannah Dickson, I study Commercial Management & Quantity Surveying at Loughborough and I'm in my second year. I first got introduced to StudyStash at the start of this year, so September 2025. I didn't really know what it was, but I thought it would be useful to take notes, but I quickly realised that you can convert your notes into flashcards which is really helpful for me because it takes out that middle step, so you can start learning with them straight away. It's definitely changed my studies this year; my friends keep asking, \"Oh, what are you using?\" and I say, \"I'm using StudyStash\"."
      },
      {
        type: "quote",
        text: "It takes out that middle step, so you can start learning with them straight away.",
        author: "Hannah Dickson",
        title: "Second-year Commercial Management & Quantity Surveying Student, Loughborough",
        image: "/case-studies/hannah_dickinson.jpeg"
      },
      {
        type: "heading",
        level: 2,
        text: "Before and After StudyStash"
      },
      {
        type: "comparison-columns",
        leftTitle: "Before",
        leftText: "I do like using mind maps for my studies. At the end of last year when we had exam season, me and my friend would be shut in a room with a whiteboard and we'd have a question which we'd write on the whiteboard, we'd write everything we remembered from it and then go back to the question and look it up to see what we got right. I'd also write my notes on Word and then convert them into flashcards using other apps.",
        rightTitle: "After",
        rightText: "StudyStash allows me to convert my notes into flashcards, which is a lot easier than just writing them. With the AI, it does it for me; I upload a PowerPoint and it just converts it into notes and flashcards which is helpful. It is so easy to use. I thought it'd be complicated and I can't really deal with complicated and studying at the same time. And I can get it on my laptop, I can get it on my iPad and on my phone anytime I want, that's helpful as well, on-the-go learning."
      },
      {
        type: "heading",
        level: 2,
        text: "The impact so far"
      },
      {
        type: "paragraph",
        text: "StudyStash has made studying more convenient and quicker. Although it's all good handwriting notes, when I was doing it for my GCSEs and A levels it was so time-consuming and you just had to do that. There wasn't anything else. Now I can be more efficient and actually utilise my study material rather than just writing down notes for ages. I'm going to test myself a bit more with the test feature."
      },
      {
        type: "case-study-read-card",
        title: "Loughborough University",
        href: "/about/case-studies/loughborough-university",
        imageSrc: "/case-studies/loughborough_campus.jpg",
        imageAlt: "Loughborough University campus",
        ctaText: "Read the case study"
      },
      {
        type: "statistics",
        layout: "grid",
        stats: [
          {
            value: "Convenient",
            description: "Easy to access and use in daily study",
            color: "blue"
          },
          {
            value: "Innovative",
            description: "AI support for turning content into revision assets",
            color: "green"
          },
          {
            value: "Efficient",
            description: "Less time writing notes, more time actively revising",
            color: "purple"
          }
        ]
      },
      {
        type: "heading",
        level: 2,
        text: "Advocate or avoid?"
      },
      {
        type: "paragraph",
        text: "I hope more students get to use it, because it's so helpful. Although the platform highlights that it's AI, it's based on your own notes and lecturer content; if you write your notes right, then you're going to get your answer right. The amount you put into the platform, it gives you back."
      },
      {
        type: "paragraph",
        text: "I've already recommended it to my friends because it's just so helpful for me to see my notes easily displayed. It's been really helpful so far making flashcards, and I'll be using the tests in May when I get to my exams."
      }
    ],
    seo: {
      title: "Hannah's Success Story: Perfecting Her Study Process with StudyStash",
      description: "Hannah Dickson, a second-year quantity surveying student at Loughborough, shares how StudyStash helped her create flashcards faster, study on the go, and build more efficient revision routines.",
      canonical: "https://studystash.com/about/case-studies/hannahs-story"
    },
    relatedContent: ["loughborough-university", "temis-story", "jaimees-story"]
  },
  "university-of-latvia-press-release": {
    id: "university-of-latvia-press-release",
    type: "case-study",
    category: "partnership",
    title: "University of Latvia launches pilot project for AI-based student learning support tool StudyStash",
    description: "The University of Latvia is piloting StudyStash with lecturers and students to evaluate how AI can responsibly support higher education learning outcomes.",
    slug: "university-of-latvia-press-release",
    publishDate: "2026-04-02",
    readingTime: "8 min read",
    author: {
      name: "Ben Ward",
      image: "/team/ben.jpeg"
    },
    hero: {
      image: "/case-studies/university_of_latvia.jpg",
      imageAlt: "University of Latvia press release announcement"
    },
    contentBlocks: [
      {
        type: "paragraph",
        text: "The University of Latvia (LU) is launching the testing of a student learning support tool based on artificial intelligence (AI) solutions. As part of a pilot project in collaboration with the developers of StudyStash, university lecturers and students will have the opportunity to evaluate in practice how AI solutions can support the student learning process in higher education."
      },
      {
        type: "quote",
        text: "University of Latvia is delighted to announce a new partnership with an innovative digital tool and study support provider, StudyStash. UL consistently develops a study environment that is open to new digital solutions and innovations. Testing AI tools in the study process is an important step toward modern, student-centered higher education. At the same time, we emphasise that sustainable and meaningful digital solutions can only emerge through close collaboration with students and lecturers - those who will use these tools on a daily basis. It is precisely this co-creation that allows not only the introduction of technologies but also ensures that they provide real added value to the learning process.",
        author: "Professor Kristīne Strada-Rozenberga",
        title: "LU Vice-Rector for Studies",
        image: "/case-studies/k_strada-rozenberga.jpg"
      },
      {
        type: "heading",
        level: 2,
        text: "Pilot objectives and scope"
      },
      {
        type: "paragraph",
        text: "During the testing of the learning tool, it will be assessed how personalised and adaptive AI tools can complement existing course materials in a bilingual model - Latvian and English - and what their practical impact is on the study experience."
      },
      {
        type: "paragraph",
        text: "LU sees this pilot project as an opportunity to practically evaluate how adaptive learning tools can be used responsibly, meaningfully, and with measurable added value across different academic disciplines."
      },
      {
        type: "paragraph",
        text: "The collaboration with StudyStash developers is being implemented as a pilot project aimed at gaining evidence-based insights into the effectiveness of AI tools in the context of Latvian higher education. Special attention is being paid to ensuring that new digital solutions serve as support for students' independent learning processes rather than replacing academic dialogue or the role of the lecturer."
      },
      {
        type: "paragraph",
        text: "A key part of the project is the active involvement of students - their experience, feedback, and evaluations will be among the main criteria in assessing the usefulness of the tool and its potential future development."
      },
      {
        type: "heading",
        level: 2,
        text: "Moodle integration and expected outcomes"
      },
      {
        type: "paragraph",
        text: "Integration of the StudyStash platform with LU's e-learning environment, Moodle, will allow students to access their study materials in one place and transform them into various feedback and self-assessment formats - summaries, flashcards, quizzes, and a continuously available AI assistant."
      },
      {
        type: "paragraph",
        text: "During testing, it will be analysed whether and how such tools enhance understanding of learning content and improve self-efficacy in the learning process."
      },
      {
        type: "paragraph",
        text: "Insights gained from the pilot project will serve as a basis for future decisions on the use of digital solutions in the study process and their adaptation to the European higher education environment."
      },
      {
        type: "heading",
        level: 2,
        text: "StudyStash track record"
      },
      {
        type: "paragraph",
        text: "The StudyStash platform has been developed based on real student experiences and the need for more flexible, integrated learning tools. Experience at the University of Birmingham, where StudyStash has been implemented, shows that a significant number of students reported reduced study workload and improved academic performance. Currently, StudyStash collaborates with higher education institutions in Europe and the United States."
      },
      {
        type: "quote",
        text: "We are truly excited to collaborate with the University of Latvia to test in practice how AI-enhanced learning tools can support students in their daily studies. This collaboration allows us to learn together, analyze results, and develop solutions based on real academic experience.",
        author: "Ben Ward",
        image: "/team/ben.jpeg",
        title: "Co-founder and CEO, StudyStash"
      },
      {
        type: "paragraph",
        text: "The data storage infrastructure complies with the requirements of the European Union's General Data Protection Regulation, as well as LU regulations and internal information security procedures."
      },
      {
        type: "heading",
        level: 2,
        text: "About StudyStash"
      },
      {
        type: "paragraph",
        text: "StudyStash is a UK-based EdTech company that uses AI solutions and research data on effective learning processes to create personalized learning tools. The platform operates in more than 120 languages and is developed in accordance with principles of responsible, secure, and institutionally compliant AI use."
      }
    ],
    seo: {
      title: "University of Latvia Launches StudyStash Pilot Project | StudyStash",
      description: "The University of Latvia launches a pilot with StudyStash to evaluate AI-based, bilingual student learning support in higher education.",
      canonical: "https://studystash.com/about/case-studies/university-of-latvia-press-release"
    },
    relatedContent: ["loughborough-university", "university-of-oulu", "university-of-birmingham-press-release"]
  },
  "california-state-university-east-bay": {
    id: "california-state-university-east-bay",
    type: "case-study",
    category: "partnership",
    title: "California State University, East Bay partners with StudyStash as part of a new Student Success initiative",
    description: "California State University, East Bay has announced a partnership with StudyStash to support course outcomes, engagement, and academic success initiatives across the university.",
    slug: "california-state-university-east-bay",
    publishDate: "2026-05-12",
    readingTime: "4 min read",
    author: {
      name: "Ben Ward",
      image: "/team/ben.jpeg"
    },
    hero: {
      image: "/case-studies/csueb_hero.jpeg",
      imageAlt: "StudyStash team members with Fanny Yeung at California State University, East Bay"
    },
    contentBlocks: [
      {
        type: "paragraph",
        text: "California State University, East Bay (CSUEB) has announced a new partnership with StudyStash, an adaptive learning platform built on proven neuroscience, to support students in improving course outcomes and strengthen academic success initiatives across the university."
      },
      {
        type: "quote",
        text: "At California State University, East Bay, we approach student success through the intentional use of data, equity, and technology to strengthen learning. StudyStash reflects that commitment, offering a research-based, scalable way to enhance how students engage with course material and improve outcomes, particularly for those who benefit most from additional support. A strong sense of community and belonging remains central to the CSUEB experience, and technology plays an important role in reinforcing that by helping students feel more confident and supported in their academic work. As we pilot StudyStash across a range of high demand courses, we look forward to understanding how it can deepen learning and expand access to effective support.",
        author: "Fanny Yeung",
        title: "Associate Vice President, Institutional Effectiveness & Research, California State University, East Bay",
        image: "/case-studies/fanny_yeung.png"
      },
      {
        type: "heading",
        level: 2,
        text: "About California State University, East Bay"
      },
      {
        type: "paragraph",
        text: "Founded in 1957 and home to approximately 11,544 students, California State University, East Bay has been recognized as a \"Best in the West\" college by The Princeton Review and is a federally designated Asian American, Native American, Pacific Islander Serving Institution (AANAPISI) and Hispanic Serving Institution (HSI), reflecting its deep commitment to equitable, accessible education."
      },
      {
        type: "paragraph",
        text: "With a student-to-faculty ratio of 17:1, the university has long prioritized personalized, high-quality learning for its diverse student community."
      },
      {
        type: "heading",
        level: 2,
        text: "Embedding Adaptive Study Support in Canvas"
      },
      {
        type: "paragraph",
        text: "The implementation will integrate StudyStash directly within CSUEB's online learning environment, Canvas, with a focus on boosting course engagement and reducing DFW (Drop-out, Failure, and Withdrawal) rates across high-enrollment courses."
      },
      {
        type: "paragraph",
        text: "Following successful rollouts across a growing number of higher education institutions, the focus at CSUEB will be on student confidence, academic persistence, and equitable access to support."
      },
      {
        type: "quote",
        text: "CSUEB's new student success initiative, focused on reducing DFW rates and boosting course engagement, is exactly the kind of pragmatic, proactive approach to AI that will ultimately benefit their students. We are proud to be part of that mission.",
        author: "Ben Ward",
        title: "CEO, StudyStash",
        image: "/team/ben.jpeg",
        logo: "/favicon-32x32.png"
      },
      {
        type: "heading",
        level: 2,
        text: "Supporting High-Demand Courses"
      },
      {
        type: "paragraph",
        text: "StudyStash will be available across high-demand courses where support has the greatest impact, spanning the College of Letters, Arts & Social Sciences, the College of Science, and the College of Business & Economics."
      },
      {
        type: "paragraph",
        text: "The pilot will cover subjects including Communication, Statistics, Accounting, and Economics. Students will gain access to personalized flashcards, practice questions, and spaced repetition tools, all informed by the latest research in cognitive science."
      },
      {
        type: "heading",
        level: 2,
        text: "Expanding StudyStash's US Higher Education Partnerships"
      },
      {
        type: "paragraph",
        text: "The partnership represents StudyStash's continued expansion across US higher education, bringing adaptive learning technology to the students who stand to benefit most."
      },
      {
        type: "heading",
        level: 2,
        text: "About StudyStash"
      },
      {
        type: "paragraph",
        text: "StudyStash is a UK-based EdTech company that uses AI and cognitive science to make studying more effective, inclusive, and personalized. The platform automatically generates study tools from course materials in 121 languages, helping students stay motivated and organized, while ensuring all AI use remains responsible, secure, and aligned with institutional standards."
      },
      {
        type: "sources",
        title: "Sources & References",
        items: [
          {
            id: "csueb-facts",
            title: "Cal State East Bay Facts",
            date: "2026",
            url: "https://www.csueastbay.edu/about/facts.html",
            type: "website",
            publication: "California State University, East Bay",
            description: "Institutional background on CSUEB, including serving-institution designations and student profile details."
          },
          {
            id: "csueb-why",
            title: "Why CSUEB?",
            date: "2026",
            url: "https://www.csueastbay.edu/iao/future-students/why-csueb.html",
            type: "website",
            publication: "California State University, East Bay",
            description: "Overview of CSUEB's academic recognition, including The Princeton Review's Best in the West recognition."
          },
          {
            id: "csueb-college-board",
            title: "California State University: East Bay Academics",
            date: "2026",
            url: "https://bigfuture.collegeboard.org/colleges/california-state-university-east-bay/academics",
            type: "website",
            publication: "College Board BigFuture",
            description: "College profile data including the 17:1 student-to-faculty ratio."
          }
        ]
      }
    ],
    seo: {
      title: "California State University, East Bay partners with StudyStash | StudyStash",
      description: "California State University, East Bay partners with StudyStash to support student success, course engagement, and equitable access to adaptive study support.",
      canonical: "https://studystash.com/about/case-studies/california-state-university-east-bay"
    },
    relatedContent: ["pikes-peak-state-college", "eastern-washington-university", "bowling-green-state-university"]
  },
  "pikes-peak-state-college": {
    id: "pikes-peak-state-college",
    type: "case-study",
    category: "partnership",
    title: "Pikes Peak State College partners with StudyStash to support student retention and academic success",
    description: "Pikes Peak State College has announced a partnership with StudyStash to support student retention, engagement, and academic success through adaptive study tools in D2L Brightspace.",
    slug: "pikes-peak-state-college",
    publishDate: "2026-05-07",
    readingTime: "5 min read",
    author: {
      name: "Ben Ward",
      image: "/team/ben.jpeg"
    },
    hero: {
      image: "/case-studies/ppsc_hero.jpg",
      imageAlt: "Pikes Peak State College sign with Pikes Peak in the background"
    },
    contentBlocks: [
      {
        type: "paragraph",
        text: "Pikes Peak State College has announced a new partnership with StudyStash, an adaptive learning platform built on proven neuroscience, to support student retention, engagement, and academic success across the college."
      },
      {
        type: "quote",
        text: "StudyStash's integration into our LMS made it a stand-out choice for Pikes Peak State College. D2L integration eliminates additional steps for students to access study tools and saves faculty and instructors valuable time.",
        author: "Rachael Campbell",
        title: "Director of Retention, Pikes Peak State College",
        image: "/case-studies/rachael_campbell.png"
      },
      {
        type: "heading",
        level: 2,
        text: "About Pikes Peak State College"
      },
      {
        type: "paragraph",
        text: "Founded in 1968 and now the largest institution of higher education in the Pikes Peak region, Pikes Peak State College serves a diverse community of students across multiple campuses in Colorado Springs. In 2022, the college expanded its mission and changed its name from Pikes Peak Community College to Pikes Peak State College, reflecting a broadened commitment to offering bachelor's degree programs alongside its deep tradition of accessible, career-focused education."
      },
      {
        type: "paragraph",
        text: "With more than 150 programs spanning liberal arts, sciences, business, health, and technical education, PPSC is built around the belief that high-quality learning should be open to all."
      },
      {
        type: "heading",
        level: 2,
        text: "Embedding Adaptive Study Support in D2L Brightspace"
      },
      {
        type: "paragraph",
        text: "The implementation will integrate StudyStash directly within PPSC's D2L Brightspace learning environment, providing students with personalized, adaptive study tools that respond to their individual learning needs. The partnership launches at the start of the Summer 2026 term, with an initial focus on Biology, World Mythology, and Communication in Pop Culture."
      },
      {
        type: "paragraph",
        text: "These courses were selected for their breadth across the student body and the opportunity StudyStash offers to support engagement and confidence at critical points in the academic year. Students will gain access to personalized flashcards, practice questions, and spaced repetition tools, all generated automatically from course materials and informed by the latest research in cognitive science."
      },
      {
        type: "quote",
        text: "The partnership with StudyStash reflects Pikes Peak's commitment to reducing barriers and creating more equitable pathways to student success. StudyStash enhances our ability to provide impactful, personalized academic support and helps ensure more students make it to the finish line.",
        author: "Jessi Allen-Lutz, M.A.",
        title: "Assistant Director of Retention, Pikes Peak State College",
        image: "/case-studies/jessi_allen_lutz.jpg"
      },
      {
        type: "heading",
        level: 2,
        text: "Reducing Barriers to Student Success"
      },
      {
        type: "paragraph",
        text: "The partnership also reflects PPSC's ongoing investment in inclusive student support. The college's ASPIRE Scholars Program - Achieving Success through Personalized Inclusive Real-World Education - and its Promise coaching program both speak to a broader institutional commitment to ensuring that every student, regardless of background or ability, has the tools they need to succeed."
      },
      {
        type: "paragraph",
        text: "StudyStash's multilingual support and accessibility-first design sit naturally alongside these initiatives, helping students build confidence, stay organized, and take ownership of their learning journey."
      },
      {
        type: "quote",
        text: "Pikes Peak State College is exactly the kind of institution where StudyStash can have meaningful impact: where cost shouldn't be a barrier, and where smart, personalised tools can help every student reach their potential. It's part of a growing commitment to working with community colleges - and we couldn't think of a better place to start than right at the foot of the Rockies!",
        author: "Ben Ward",
        title: "CEO, StudyStash",
        image: "/team/ben.jpeg",
        logo: "/favicon-32x32.png"
      },
      {
        type: "heading",
        level: 2,
        text: "Expanding StudyStash's Community College Partnerships"
      },
      {
        type: "paragraph",
        text: "The partnership marks StudyStash's first presence in Colorado Springs and reflects a growing commitment to working with community colleges across the United States."
      },
      {
        type: "paragraph",
        text: "The Pikes Peak partnership represents StudyStash's continued expansion across US higher education, bringing adaptive learning technology to the students who stand to benefit most - and to institutions that are serious about turning access into achievement."
      },
      {
        type: "heading",
        level: 2,
        text: "About StudyStash"
      },
      {
        type: "paragraph",
        text: "StudyStash is a UK-based EdTech company that uses AI and cognitive science to make studying more effective, inclusive, and personalised. The platform automatically generates study tools from course materials in 121 languages, helping students stay motivated and organised, while ensuring all AI use remains responsible, secure, and aligned with institutional standards."
      },
      {
        type: "sources",
        title: "Sources & References",
        items: [
          {
            id: "ppsc-about",
            title: "About Pikes Peak State College",
            date: "2026",
            url: "https://www.pikespeak.edu/about/index.php",
            type: "website",
            publication: "Pikes Peak State College",
            description: "Institutional background on Pikes Peak State College, including its 1968 founding, mission expansion, and role in Southern Colorado higher education."
          },
          {
            id: "ppsc-aspire",
            title: "ASPIRE Scholars at PPSC",
            date: "2026",
            url: "https://www.pikespeak.edu/landing-pages/aspire.php",
            type: "website",
            publication: "Pikes Peak State College",
            description: "Overview of PPSC's ASPIRE Scholars Program and its focus on inclusive, personalized student support."
          },
          {
            id: "ppsc-promise",
            title: "Promise Programs",
            date: "2026",
            url: "https://www.pikespeak.edu/costs-scholarships-aid/promise-programs/index.php",
            type: "website",
            publication: "Pikes Peak State College",
            description: "Details on PPSC's Promise Programs, including coaching and support services for eligible students."
          },
          {
            id: "ppsc-hero-image",
            title: "Pikes Peak State College sign",
            authors: ["Jeffrey Beall"],
            date: "2022",
            url: "https://commons.wikimedia.org/wiki/File:Pikes_Peak_State_College_sign.JPG",
            type: "website",
            publication: "Wikimedia Commons",
            description: "Hero image used under Creative Commons Attribution 4.0 International license."
          }
        ]
      }
    ],
    seo: {
      title: "Pikes Peak State College partners with StudyStash | StudyStash",
      description: "Pikes Peak State College partners with StudyStash to support student retention, engagement, and academic success through adaptive study tools in D2L Brightspace.",
      canonical: "https://studystash.com/about/case-studies/pikes-peak-state-college"
    },
    relatedContent: ["eastern-washington-university", "bowling-green-state-university", "st-marys-university"]
  },
  "eastern-washington-university": {
    id: "eastern-washington-university",
    type: "case-study",
    category: "partnership",
    title: "Eastern Washington University partners with StudyStash to support student success and degree completion",
    description: "Eastern Washington University has announced a partnership with StudyStash to strengthen student success initiatives and support degree completion with adaptive learning.",
    slug: "eastern-washington-university",
    publishDate: "2026-04-21",
    readingTime: "5 min read",
    author: {
      name: "Ben Ward",
      image: "/team/ben.jpeg"
    },
    hero: {
      image: "/case-studies/ewu_hero.jpg",
      imageAlt: "Eastern Washington University partnership announcement"
    },
    contentBlocks: [
      {
        type: "paragraph",
        text: "Eastern Washington University has announced a new partnership with StudyStash, an adaptive learning platform built on proven neuroscience, to support students in completing their degrees and strengthen academic success initiatives across the university."
      },
      {
        type: "quote",
        text: "We are excited to explore this partnership with StudyStash as part of our broader work to support student success and degree completion at Eastern Washington University. What I appreciate about StudyStash is that it gives students an additional way to check their understanding and see where they are doing well and where they may need more support. That kind of insight can help students feel more confident, stay engaged, and take greater ownership of their learning.",
        author: "Gina Gendusa",
        title: "Director, Student Success & Degree Completion, Eastern Washington University",
        image: "/case-studies/gina_ewu.png"
      },
      {
        type: "heading",
        level: 2,
        text: "About Eastern Washington University"
      },
      {
        type: "paragraph",
        text: "Founded in 1882 and home to about 10,000 students, Eastern Washington University has received a \"Higher Access, Higher Earnings\" designation as an Opportunity College and University by the Carnegie Foundation and has been recognized as a College of Distinction for its commitment to undergraduate success. With a student-to-faculty ratio of 19:1, the university is known as the region's polytechnic for its commitment to applied learning and career readiness."
      },
      {
        type: "heading",
        level: 2,
        text: "Implementation and Student Support Focus"
      },
      {
        type: "paragraph",
        text: "The implementation will integrate StudyStash directly within Eastern's online learning environment, supporting the university's Degree Completion program and providing students with personalized, adaptive study tools that respond to their individual needs. Following successful rollouts across a growing number of higher education institutions, the focus at Eastern Washington will be on student wellbeing, academic confidence, and equitable access to support."
      },
      {
        type: "paragraph",
        text: "StudyStash will be available across high-demand modules where support has the greatest impact, including Mathematics, Psychology, and Chemistry. Students will gain access to personalized flashcards, practice questions, and spaced repetition tools, all informed by the latest research in cognitive science. The partnership represents StudyStash's continued expansion across US higher education, bringing adaptive learning technology to the students who stand to benefit most."
      },
      {
        type: "quote",
        text: "When students feel genuinely supported in their studies, something shifts. They develop a sense of belonging, engage more deeply with their learning community, and that confidence is what drives engagement and ultimately, progression.",
        author: "Ben Ward",
        title: "CEO, StudyStash",
        image: "/team/ben.jpeg",
        logo: "/favicon-32x32.png"
      },
      {
        type: "heading",
        level: 2,
        text: "About StudyStash"
      },
      {
        type: "paragraph",
        text: "StudyStash is a UK-based EdTech company that uses AI and cognitive science to make studying more effective, inclusive, and personalized. The platform automatically generates study tools from course materials in 121 languages, helping students stay motivated and organized, while ensuring all AI use remains responsible, secure, and aligned with institutional standards."
      }
    ],
    seo: {
      title: "Eastern Washington University partners with StudyStash | StudyStash",
      description: "Eastern Washington University has announced a new partnership with StudyStash to support student success and degree completion with adaptive, neuroscience-backed learning support.",
      canonical: "https://studystash.com/about/case-studies/eastern-washington-university"
    },
    relatedContent: ["bowling-green-state-university", "university-of-texas-at-dallas", "loughborough-university"]
  }
};
